import React, { useMemo, useState } from 'react';
import {
  BadgeCheck,
  Banknote,
  CalendarDays,
  CreditCard,
  Download,
  Eye,
  FileText,
  Landmark,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Pencil,
  Plus,
  Receipt,
  Save,
  ShieldCheck,
  User,
  MessageCircle,
  UserRoundCog,
  Users,
  Wallet,
  X,
  Trash2,
} from 'lucide-react';
import Logo from './components/Logo';
import './App.css';

const STORAGE_KEY = 'sabar-garden-portal-v2';
const SESSION_KEY = `${STORAGE_KEY}-session`;
const ACTIVE_KEY = `${STORAGE_KEY}-active`;
const PENDING_SYNC_KEY = `${STORAGE_KEY}-pending-sync`;
const APP_VERSION = '2026-05-23-cache-sync-v19';
const VERSION_KEY = `${STORAGE_KEY}-app-version`;
const APP_CONFIG = window.SABAR_GARDEN_CONFIG || {};
const API_BASE_URL = String(APP_CONFIG.apiBaseUrl || '').replace(/\/$/, '');
const isApiEnabled = Boolean(API_BASE_URL);

async function apiRequest(path, options = {}) {
  if (!isApiEnabled) return null;
  const method = String(options.method || 'GET').toUpperCase();
  const cacheBustedPath =
    method === 'GET' ? `${path}${path.includes('?') ? '&' : '?'}_=${Date.now()}` : path;
  const response = await fetch(`${API_BASE_URL}${cacheBustedPath}`, {
    ...options,
    cache: 'no-store',
    headers: {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.success === false) {
    throw new Error(payload.message || 'API request failed');
  }
  return payload;
}

async function loadRemoteData() {
  const response = await apiRequest('/portal.php?action=state');
  return response?.data ? normalizeData(response.data) : null;
}

async function saveRemoteData(nextData) {
  return apiRequest('/portal.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'save_state', data: nextData }),
  });
}

async function remoteLogin(credentials) {
  return apiRequest('/portal.php', {
    method: 'POST',
    body: JSON.stringify({ action: 'login', ...credentials }),
  });
}

async function uploadFileToServer(file) {
  if (!isApiEnabled) return null;
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiRequest('/upload.php', { method: 'POST', body: formData });
  return response?.file || null;
}

async function startGatewayPayment(payload) {
  return apiRequest('/payment_gateway.php', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

const defaultCommitteeName = 'কার্যনির্বাহী কমিটি';

const adminModules = [
  { id: 'dashboard', label: 'ড্যাশবোর্ড', actions: ['view'] },
  { id: 'members', label: 'সদস্য', actions: ['view', 'add', 'edit', 'delete'] },
  { id: 'requests', label: 'রিকোয়েস্ট', actions: ['view', 'approve'] },
  { id: 'payments', label: 'পেমেন্ট', actions: ['view', 'add', 'approve', 'delete'] },
  { id: 'expenses', label: 'খরচ', actions: ['view', 'add', 'delete'] },
  { id: 'reports', label: 'রিপোর্ট', actions: ['view'] },
  { id: 'land', label: 'জমি প্রকল্প', actions: ['view', 'add', 'edit', 'delete'] },
  { id: 'committee', label: 'কমিটি', actions: ['view', 'add', 'edit', 'delete'] },
  { id: 'advice', label: 'পরামর্শ', actions: ['view'] },
  { id: 'notices', label: 'নোটিশ', actions: ['view', 'add', 'edit', 'delete'] },
  { id: 'adminUsers', label: 'এডমিন ইউজার', actions: ['view', 'add', 'edit', 'delete'] },
];

const actionLabels = {
  view: 'ভিউ',
  add: 'এড',
  edit: 'এডিট',
  delete: 'ডিলিট',
  approve: 'এপ্রুভ',
};

const committeePositions = [
  'সভাপতি',
  'সহ-সভাপতি',
  'সদস্য সচিব',
  'কোষাধ্যক্ষ',
  'প্রধান সমন্বয়ক',
  'সহকারী সমন্বয়ক',
  'ভূমি যাচাই প্রতিনিধি',
  'দলিল ও রেজিস্ট্রেশন প্রতিনিধি',
  'বাজার বিশ্লেষণ প্রতিনিধি',
  'নথিপত্র ও রেকর্ড প্রতিনিধি',
  'সদস্য প্রতিনিধি সমন্বয়ক',
  'সদস্য',
];

const initialData = {
  members: [
    {
      id: 1,
      userId: 'SG0001',
      name: 'মোঃ সোলাইমান',
      father: 'মোঃ আব্দুল করিম',
      mother: 'মোসাঃ রহিমা বেগম',
      mobile: '01700000001',
      photo: '',
      attachment: null,
      nid: '1234567890',
      email: 'member@sabargarden.com',
      profession: 'ব্যবসা',
      religion: 'ইসলাম',
      permanentAddress: 'রংপুর, বাংলাদেশ',
      currentAddress: 'ঢাকা, বাংলাদেশ',
      emergency: 'মোঃ করিম - 01700000002',
      investment: 50000,
      status: 'active',
      joinedAt: '2026-05-07',
      nominee: {
        name: 'মোসাঃ আয়েশা বেগম',
        relation: 'স্ত্রী',
        father: 'মোঃ রহমান',
        mobile: '01700000003',
        nid: '9876543210',
        photo: '',
        address: 'ঢাকা, বাংলাদেশ',
      },
    },
  ],
  payments: [
    {
      id: 1,
      memberId: 1,
      type: 'প্রাথমিক বিনিয়োগ',
      amount: 50000,
      date: '2026-05-07',
      method: 'ব্যাংক ট্রান্সফার',
      trxId: 'UCB-DEMO-001',
      status: 'approved',
      note: 'সদস্য নিবন্ধনের সময় জমা',
    },
    {
      id: 2,
      memberId: 1,
      type: 'মাসিক চাঁদা',
      amount: 5000,
      date: '2026-05-15',
      method: 'ব্যাংক ট্রান্সফার',
      trxId: 'UCB-DEMO-002',
      status: 'approved',
      note: 'মে মাসের চাঁদা',
    },
  ],
  expenses: [
    {
      id: 1,
      title: 'জমির কাগজপত্র যাচাই',
      category: 'আইনগত যাচাই',
      amount: 8000,
      date: '2026-05-20',
      method: 'ব্যাংক ট্রান্সফার',
      approvedBy: 'কার্যনির্বাহী কমিটি',
      note: 'খতিয়ান, দলিল ও নামজারি যাচাই খরচ',
    },
  ],
  landProjects: [
    {
      id: 1,
      name: 'সবর গার্ডেন জমি প্রকল্প ১',
      location: 'সম্ভাব্য উন্নয়ন এলাকা',
      size: 'প্রক্রিয়াধীন',
      status: 'যাচাই চলছে',
      budget: 2000000,
      checks: ['খতিয়ান', 'নামজারি', 'দলিল', 'ভূমি উন্নয়ন কর', 'দখল অবস্থা'],
    },
  ],
  notices: [
    {
      id: 1,
      title: 'মাসিক সাধারণ সভা',
      date: '2026-03-01',
      body: 'প্রতি মাসে অন্তত ১টি সাধারণ সভা অনুষ্ঠিত হবে। গুরুত্বপূর্ণ সিদ্ধান্তে ৬০% উপস্থিতি প্রয়োজন।',
    },
  ],
  advice: [],
  memberRequests: [],
  committeeDetails: {
    [defaultCommitteeName]: {
      work: 'প্রকল্প পরিচালনা, সভা আহ্বান, সিদ্ধান্ত বাস্তবায়ন, হিসাব ও রিপোর্ট সদস্যদের সামনে উপস্থাপন।',
      policy: 'স্বচ্ছতা, জবাবদিহিতা, সংখ্যাগরিষ্ঠ মতামত এবং প্রকল্পের নীতিমালা অনুসরণ বাধ্যতামূলক।',
      duties: 'জমি যাচাই, বিনিয়োগ পরিকল্পনা অনুমোদন, ব্যাংক হিসাব তদারকি এবং আইনগত কার্যক্রম সম্পন্ন করা।',
      authority: 'বড় আর্থিক সিদ্ধান্তে সাধারণ সদস্যদের মতামত নিতে হবে এবং সকল সিদ্ধান্ত লিখিত রেজুলেশন আকারে সংরক্ষণ করতে হবে।',
    },
    'জমি ক্রয়-বিক্রয় কমিটি': {
      work: 'জমি ক্রয়-বিক্রয়, দলিল সম্পাদন, রেজিস্ট্রেশন ও সংশ্লিষ্ট প্রশাসনিক কার্যক্রম পরিচালনা।',
      policy: 'কার্যনির্বাহী কমিটির অনুমোদন ছাড়া জমি ক্রয়-বিক্রয় করা যাবে না।',
      duties: 'খতিয়ান, নামজারি, দলিল, দখল অবস্থা, মামলা বা নিষেধাজ্ঞা যাচাই এবং সদস্যদের প্রতিবেদন দেয়া।',
      authority: 'প্রকল্পের পক্ষে ভূমি অফিস, সাব-রেজিস্ট্রি অফিস, ব্যাংক ও সরকারি প্রতিষ্ঠানে প্রতিনিধিত্ব করা।',
    },
  },
};

const currency = (value) =>
  new Intl.NumberFormat('bn-BD', { style: 'currency', currency: 'BDT', maximumFractionDigits: 0 }).format(
    Number(value || 0)
  );

const today = () => new Date().toISOString().slice(0, 10);

function toBanglaDigits(value) {
  return String(value).replace(/\d/g, (digit) => '০১২৩৪৫৬৭৮৯'[digit]);
}

function formatBanglaDate(dateString) {
  if (!dateString) return '';
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateString;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${toBanglaDigits(y)}-${toBanglaDigits(m)}-${toBanglaDigits(d)}`;
}

function normalizeData(rawData) {
  const data = rawData || initialData;

  return {
    ...initialData,
    ...data,
    members: data.members || [],
    payments: data.payments || [],
    expenses: data.expenses || [],
    landProjects: (data.landProjects || []).map((project) => ({
      ...project,
      documents: [
        ...(project.documents || []),
        ...(project.attachment && !(project.documents || []).some((doc) => doc.name === project.attachment.name)
          ? [project.attachment]
          : []),
      ],
    })),
    notices: data.notices || [],
    advice: data.advice || [],
    memberRequests: data.memberRequests || [],
    adminUsers: data.adminUsers || [],
    committeeDetails: {
      ...initialData.committeeDetails,
      ...(data.committeeDetails || {}),
    },
    committeeMembers: (data.committeeMembers || []).map((member) => ({
      committeeName: defaultCommitteeName,
      ...member,
    })),
  };
}

function loadData() {
  try {
    if (isApiEnabled && localStorage.getItem(VERSION_KEY) !== APP_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(PENDING_SYNC_KEY);
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      return normalizeData(initialData);
    }
    return normalizeData(JSON.parse(localStorage.getItem(STORAGE_KEY)) || initialData);
  } catch {
    return normalizeData(initialData);
  }
}

function markPendingSync(value) {
  try {
    if (value) localStorage.setItem(PENDING_SYNC_KEY, '1');
    else localStorage.removeItem(PENDING_SYNC_KEY);
  } catch {
    // ignore storage errors
  }
}

function hasPendingSync() {
  try {
    return localStorage.getItem(PENDING_SYNC_KEY) === '1';
  } catch {
    return false;
  }
}

function App() {
  const [data, setData] = useState(loadData);
  const [syncStatus, setSyncStatus] = useState(isApiEnabled ? 'connecting' : 'local');
  const [session, setSession] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SESSION_KEY)) || null;
    } catch {
      return null;
    }
  });
  const [loginError, setLoginError] = useState('');

  const latestNotice = React.useMemo(() => {
    if (!data.notices?.length) return null;
    return [...data.notices].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  }, [data.notices]);

  const saveData = (nextData) => {
    const normalized = normalizeData(nextData);
    setData(normalized);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    if (isApiEnabled) {
      markPendingSync(true);
      setSyncStatus('saving');
      saveRemoteData(normalized)
        .then(() => {
          markPendingSync(false);
          setSyncStatus('synced');
        })
        .catch(() => setSyncStatus('offline'));
    }
  };

  React.useEffect(() => {
    if (!isApiEnabled) return;
    let alive = true;
    loadRemoteData()
      .then((remoteData) => {
        if (!alive) return;
        if (remoteData) {
          setData(remoteData);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(remoteData));
          localStorage.setItem(VERSION_KEY, APP_VERSION);
          markPendingSync(false);
          setSyncStatus('synced');
        } else {
          const localData = loadData();
          markPendingSync(false);
          saveRemoteData(loadData()).then(() => {
            if (!alive) return;
            setData(localData);
            setSyncStatus('synced');
          });
        }
      })
      .catch(() => {
        if (alive) setSyncStatus('offline');
      });
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    if (!isApiEnabled) return;
    const syncLocalToServer = () => {
      if (!hasPendingSync()) return;
      setSyncStatus('saving');
      saveRemoteData(loadData())
        .then(() => {
          markPendingSync(false);
          setSyncStatus('synced');
        })
        .catch(() => setSyncStatus('offline'));
    };
    const timer = setInterval(syncLocalToServer, 30000);
    window.addEventListener('online', syncLocalToServer);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', syncLocalToServer);
    };
  }, []);

  // persist session so refresh doesn't log out
  React.useEffect(() => {
    try {
      if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      else localStorage.removeItem(SESSION_KEY);
    } catch (e) {
      // ignore
    }
  }, [session]);

  const handleLogin = async ({ userId, password }) => {
    const normalized = userId.trim().toUpperCase();
    if (isApiEnabled) {
      try {
        const response = await remoteLogin({ userId: normalized, password });
        if (response?.session) {
          setSession(response.session);
          setLoginError('');
          return;
        }
      } catch (error) {
        setSyncStatus('offline');
      }
    }

    if (normalized === 'ADMIN' && password === 'admin123') {
      setSession({ role: 'admin', name: 'Super Admin', isSuperAdmin: true });
      setLoginError('');
      return;
    }

    const adminUser = (data.adminUsers || []).find(
      (item) => item.status !== 'inactive' && String(item.userId || '').trim().toUpperCase() === normalized
    );
    if (adminUser && String(adminUser.password || '') === password) {
      setSession({ role: 'admin', name: adminUser.name, adminUserId: adminUser.id, isSuperAdmin: false });
      setLoginError('');
      return;
    }

    const member = data.members.find((item) => item.userId.toUpperCase() === normalized);
    if (member && (member.nid === password || password === '123456')) {
      setSession({ role: 'member', memberId: member.id, name: member.name });
      setLoginError('');
      return;
    }

    setLoginError('ইউজার আইডি বা পাসওয়ার্ড সঠিক নয়।');
  };

  const handleRegistrationRequest = (payload) => {
    saveData({
      ...data,
      memberRequests: [
        {
          id: Date.now(),
          type: 'registration',
          status: 'pending',
          submittedAt: today(),
          payload,
        },
        ...data.memberRequests,
      ],
    });
  };

  if (!session) {
    return (
      <LoginScreen
        onLogin={handleLogin}
        onRegisterRequest={handleRegistrationRequest}
        error={loginError}
        latestNotice={latestNotice}
        syncStatus={syncStatus}
      />
    );
  }

  if (session.role === 'admin') {
    return <AdminPortal data={data} saveData={saveData} session={session} syncStatus={syncStatus} onLogout={() => setSession(null)} />;
  }

  return (
    <MemberPortal
      data={data}
      saveData={saveData}
      syncStatus={syncStatus}
      member={data.members.find((item) => item.id === session.memberId)}
      onLogout={() => setSession(null)}
    />
  );
}

function LoginScreen({ onLogin, onRegisterRequest, error, latestNotice, syncStatus }) {
  const [form, setForm] = useState({ userId: '', password: '' });
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({});
  const [registerMessage, setRegisterMessage] = useState('');

  React.useEffect(() => {
    // clear any values possibly injected by browser autofill
    setForm({ userId: '', password: '' });
  }, []);

  return (
    <main className="login-page bangla">
      {latestNotice && (
        <section className="login-notice-panel mb-4">
          <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-yellow-800">{latestNotice.title || 'নোটিশ'}</p>
                {latestNotice.date && (
                  <p className="text-xs text-gray-500">{formatBanglaDate(latestNotice.date)}</p>
                )}
              </div>
            </div>
            {latestNotice.body && <p className="mt-3 text-sm text-gray-700">{latestNotice.body}</p>}
          </div>
        </section>
      )}
      {!showRegister && (
      <section className="login-panel">
        <div className="brand-mark" aria-label="সবর গার্ডেন লোগো">
          <Logo size={80} />
        </div>
        <h1>সবর গার্ডেন</h1>
        <p>সম্মিলিত শক্তি, নিরাপদ বিনিয়োগ</p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            onLogin(form);
          }}
          className="login-form"
          autoComplete="off"
        >
          {/* dummy fields to discourage browser autofill */}
          <input type="text" name="fakeusernameremembered" style={{ display: 'none' }} />
          <input type="password" name="fakepasswordremembered" style={{ display: 'none' }} />
          <label>
            ইউজার আইডি
            <input
              name="login_uid"
              value={form.userId}
              onChange={(event) => setForm({ ...form, userId: event.target.value })}
              placeholder=""
              autoComplete="off"
              readOnly
              onFocus={(e) => (e.currentTarget.readOnly = false)}
              required
            />
          </label>
          <label>
            পাসওয়ার্ড / NID
            <input
              type="password"
              name="login_pwd"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              placeholder=""
              autoComplete="new-password"
              readOnly
              onFocus={(e) => (e.currentTarget.readOnly = false)}
              required
            />
          </label>
          {error && <div className="error-box">{error}</div>}
          {registerMessage && <div className="success-box">{registerMessage}</div>}
          <div className={`sync-box ${syncStatus}`}>{syncStatusLabel(syncStatus)}</div>
          <button className="primary-button" type="submit">
            লগইন করুন
          </button>
        </form>

        
        <button className="secondary-button full-button" type="button" onClick={() => setShowRegister(!showRegister)}>
          নতুন সদস্য নিবন্ধন আবেদন
        </button>
      </section>
      )}
      {showRegister && (
        <section className="login-panel register-panel">
          <h2>সদস্য নিবন্ধন আবেদন</h2>
          <p>অ্যাডমিন অনুমোদন দিলে আপনার User ID তৈরি হবে।</p>
          <MemberRequestForm
            form={registerForm}
            setForm={setRegisterForm}
            submitLabel="আবেদন পাঠান"
            onSubmit={(event) => {
              event.preventDefault();
              onRegisterRequest(registerForm);
              setRegisterForm({});
              setRegisterMessage('আপনার নিবন্ধন আবেদন অ্যাডমিন প্যানেলে পাঠানো হয়েছে।');
              setShowRegister(false);
            }}
          />
          <button className="secondary-button full-button" type="button" onClick={() => setShowRegister(false)}>
            লগইন পেজে ফিরুন
          </button>
        </section>
      )}
    </main>
  );
}

function Shell({ title, subtitle, navItems, active, setActive, onLogout, children }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="portal bangla">
      <aside className={open ? 'sidebar' : 'sidebar compact'}>
        <div className="sidebar-brand">
          <div className="sidebar-logo">
            <Logo size={46} />
          </div>
          {open && (
            <div>
              <strong>সবর গার্ডেন</strong>
              <span>নিরাপদ বিনিয়োগ</span>
            </div>
          )}
        </div>
        <nav>
          {navItems.map((item) => (
            <button key={item.id} className={active === item.id ? 'active' : ''} onClick={() => setActive(item.id)}>
              <item.icon size={20} />
              {open && <span>{item.label}</span>}
            </button>
          ))}
        </nav>
        <button className="logout-button" onClick={onLogout}>
          <LogOut size={20} />
          {open && <span>লগআউট</span>}
        </button>
      </aside>
      <section className="content">
        <header className="topbar">
          <button className="icon-button" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
        </header>
        {children}
      </section>
    </div>
  );
}

function AdminPortal({ data, saveData, session, syncStatus, onLogout }) {
  const [active, setActive] = useState(() => { try { return localStorage.getItem(ACTIVE_KEY) || 'dashboard'; } catch { return 'dashboard'; } });
  React.useEffect(() => { try { localStorage.setItem(ACTIVE_KEY, active); } catch {} }, [active]);
  const [memberForm, setMemberForm] = useState({});
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [viewingMemberId, setViewingMemberId] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ date: today(), method: 'ব্যাংক ট্রান্সফার', type: 'মাসিক চাঁদা', projectId: '' });
  const [expenseForm, setExpenseForm] = useState({ date: today(), method: 'ব্যাংক ট্রান্সফার', projectId: '' });
  const [noticeForm, setNoticeForm] = useState({ date: today() });
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [landForm, setLandForm] = useState({ checks: 'খতিয়ান, নামজারি, দলিল, ভূমি উন্নয়ন কর, দখল অবস্থা' });
  const [editingLandId, setEditingLandId] = useState(null);
  const [committeeForm, setCommitteeForm] = useState({
    committeeName: defaultCommitteeName,
    position: 'সদস্য',
    termStart: today(),
    termEnd: today(),
    status: 'active',
  });
  const [editingCommitteeId, setEditingCommitteeId] = useState(null);
  const [adminUserForm, setAdminUserForm] = useState(defaultAdminUserForm());
  const [editingAdminUserId, setEditingAdminUserId] = useState(null);

  const totals = useMemo(() => calculateTotals(data), [data]);
  const permissions = getAdminPermissions(data, session);
  const can = (moduleId, action = 'view') => hasAdminPermission(permissions, moduleId, action);
  const nav = [
    { id: 'dashboard', label: 'ড্যাশবোর্ড', icon: LayoutDashboard },
    { id: 'members', label: 'সদস্য', icon: Users },
    { id: 'requests', label: 'রিকোয়েস্ট', icon: ShieldCheck },
    { id: 'payments', label: 'পেমেন্ট', icon: CreditCard },
    { id: 'expenses', label: 'খরচ', icon: Receipt },
    { id: 'reports', label: 'রিপোর্ট', icon: FileText },
    { id: 'land', label: 'জমি প্রকল্প', icon: Landmark },
    { id: 'committee', label: 'কমিটি', icon: UserRoundCog },
    { id: 'advice', label: 'পরামর্শ', icon: MessageCircle },
    { id: 'notices', label: 'নোটিশ', icon: FileText },
    { id: 'adminUsers', label: 'এডমিন ইউজার', icon: ShieldCheck },
  ].filter((item) => can(item.id, 'view'));

  React.useEffect(() => {
    if (!nav.some((item) => item.id === active)) {
      setActive(nav[0]?.id || 'dashboard');
    }
  }, [active, nav]);

  const saveMember = (event) => {
    event.preventDefault();
    if (editingMemberId ? !can('members', 'edit') : !can('members', 'add')) return;
    const id = editingMemberId || Date.now();
    const currentMember = data.members.find((item) => item.id === editingMemberId);
    const nextNumber = data.members.length + 1;
    const member = buildMemberFromPayload(memberForm, {
      id,
      userId: currentMember?.userId || `SG${String(nextNumber).padStart(4, '0')}`,
      status: memberForm.status || currentMember?.status || 'active',
      joinedAt: currentMember?.joinedAt || today(),
    });
    const payment =
      !editingMemberId && member.investment > 0
        ? [
            {
              id: id + 1,
              memberId: id,
              type: 'প্রাথমিক বিনিয়োগ',
              amount: member.investment,
              date: today(),
              method: 'ব্যাংক ট্রান্সফার',
              trxId: 'INITIAL',
              status: 'approved',
              note: 'সদস্য নিবন্ধনের প্রাথমিক বিনিয়োগ',
            },
          ]
        : [];
    saveData({
      ...data,
      members: editingMemberId ? data.members.map((item) => (item.id === editingMemberId ? member : item)) : [...data.members, member],
      payments: [...data.payments, ...payment],
    });
    setMemberForm({});
    setEditingMemberId(null);
  };

  const editMember = (member) => {
    if (!can('members', 'edit')) return;
    setEditingMemberId(member.id);
    setMemberForm(memberToForm(member));
    setActive('members');
  };

  const addPayment = (event) => {
    event.preventDefault();
    if (!can('payments', 'add')) return;
    saveData({
      ...data,
      payments: [
        {
          id: Date.now(),
          memberId: Number(paymentForm.memberId),
          type: paymentForm.type,
          amount: Number(paymentForm.amount || 0),
          date: paymentForm.date,
          method: paymentForm.method,
          projectId: Number(paymentForm.projectId) || null,
          trxId: paymentForm.trxId || '',
          status: paymentForm.status || 'approved',
          note: paymentForm.note || '',
        },
        ...data.payments,
      ],
    });
    setPaymentForm({ date: today(), method: 'ব্যাংক ট্রান্সফার', type: 'মাসিক চাঁদা', projectId: '' });
  };

  const addExpense = (event) => {
    event.preventDefault();
    if (!can('expenses', 'add')) return;
    saveData({
      ...data,
      expenses: [
        {
          id: Date.now(),
          title: expenseForm.title,
          category: expenseForm.category || 'সাধারণ খরচ',
          amount: Number(expenseForm.amount || 0),
          date: expenseForm.date,
          method: expenseForm.method,
          approvedBy: expenseForm.approvedBy || 'কার্যনির্বাহী কমিটি',
          projectId: Number(expenseForm.projectId) || null,
          note: expenseForm.note || '',
        },
        ...data.expenses,
      ],
    });
    setExpenseForm({ date: today(), method: 'ব্যাংক ট্রান্সফার', projectId: '' });
  };

  const addNotice = (event) => {
    event.preventDefault();
    if (editingNoticeId ? !can('notices', 'edit') : !can('notices', 'add')) return;
    if (editingNoticeId) {
      saveData({
        ...data,
        notices: data.notices.map((notice) =>
          notice.id === editingNoticeId ? { ...notice, ...noticeForm, id: editingNoticeId } : notice
        ),
      });
      setEditingNoticeId(null);
    } else {
      saveData({
        ...data,
        notices: [{ id: Date.now(), ...noticeForm }, ...data.notices],
      });
    }
    setNoticeForm({ date: today() });
  };

  const editNotice = (notice) => {
    if (!can('notices', 'edit')) return;
    setEditingNoticeId(notice.id);
    setNoticeForm({ title: notice.title, date: notice.date, body: notice.body });
    setActive('notices');
  };

  const deleteNotice = (noticeId) => {
    if (!can('notices', 'delete')) return;
    if (!window.confirm('আপনি কি এই নোটিশ মুছতে চান?')) return;
    saveData({
      ...data,
      notices: data.notices.filter((notice) => notice.id !== noticeId),
    });
    if (editingNoticeId === noticeId) {
      setEditingNoticeId(null);
      setNoticeForm({ date: today() });
    }
  };

  const saveLandProject = (event) => {
    event.preventDefault();
    if (editingLandId ? !can('land', 'edit') : !can('land', 'add')) return;
    const project = {
      id: editingLandId || Date.now(),
      name: landForm.name,
      location: landForm.location,
      size: landForm.size || 'প্রক্রিয়াধীন',
      status: landForm.status || 'যাচাই চলছে',
      budget: Number(landForm.budget || 0),
      checks: String(landForm.checks || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
      khatian: landForm.khatian || '',
      deed: landForm.deed || '',
      mutation: landForm.mutation || '',
      note: landForm.note || '',
      documents: landForm.documents || [],
    };
    saveData({
      ...data,
      landProjects: editingLandId
        ? data.landProjects.map((item) => (item.id === editingLandId ? project : item))
        : [project, ...data.landProjects],
    });
    setLandForm({ checks: 'খতিয়ান, নামজারি, দলিল, ভূমি উন্নয়ন কর, দখল অবস্থা' });
    setEditingLandId(null);
  };

  const editLandProject = (project) => {
    if (!can('land', 'edit')) return;
    setEditingLandId(project.id);
    setLandForm({
      ...project,
      checks: (project.checks || []).join(', '),
      documents: project.documents || [],
    });
    setActive('land');
  };

  const saveCommitteeMember = (event) => {
    event.preventDefault();
    if (editingCommitteeId ? !can('committee', 'edit') : !can('committee', 'add')) return;
    const selectedMember = data.members.find((item) => item.id === Number(committeeForm.memberId));
    const committeeMember = {
      id: editingCommitteeId || Date.now(),
      committeeName: committeeForm.committeeName || defaultCommitteeName,
      memberId: selectedMember?.id || Number(committeeForm.memberId) || null,
      name: selectedMember?.name || committeeForm.name,
      position: committeeForm.position,
      mobile: selectedMember?.mobile || committeeForm.mobile || '',
      userId: selectedMember?.userId || committeeForm.userId || '',
      photo: selectedMember?.photo || committeeForm.photo || '',
      nid: selectedMember?.nid || committeeForm.nid || '',
      address: selectedMember?.currentAddress || selectedMember?.permanentAddress || committeeForm.address || '',
      termStart: committeeForm.termStart || today(),
      termEnd: committeeForm.termEnd || today(),
      status: committeeForm.status || 'active',
      note: committeeForm.note || '',
    };
    saveData({
      ...data,
      committeeMembers: editingCommitteeId
        ? data.committeeMembers.map((item) => (item.id === editingCommitteeId ? committeeMember : item))
        : [committeeMember, ...data.committeeMembers],
    });
    setCommitteeForm({
      committeeName: committeeForm.committeeName || defaultCommitteeName,
      position: 'সদস্য',
      termStart: today(),
      termEnd: today(),
      status: 'active',
    });
    setEditingCommitteeId(null);
  };

  const editCommitteeMember = (member) => {
    if (!can('committee', 'edit')) return;
    setEditingCommitteeId(member.id);
    setCommitteeForm(member);
    setActive('committee');
  };

  const saveCommitteeDetails = (committeeName, details) => {
    if (!can('committee', 'edit')) return;
    saveData({
      ...data,
      committeeDetails: {
        ...(data.committeeDetails || {}),
        [committeeName]: details,
      },
    });
  };

  const approveMemberRequest = (request) => {
    if (!can('requests', 'approve')) return;
    if (request.type === 'registration') {
      const id = Date.now();
      const nextNumber = data.members.length + 1;
      const member = buildMemberFromPayload(request.payload, {
        id,
        userId: `SG${String(nextNumber).padStart(4, '0')}`,
        status: 'active',
        joinedAt: today(),
      });
      const payment =
        member.investment > 0
          ? [
              {
                id: id + 1,
                memberId: id,
                type: 'প্রাথমিক বিনিয়োগ',
                amount: member.investment,
                date: today(),
                method: 'ব্যাংক ট্রান্সফার',
                trxId: 'REG-APPROVED',
                status: 'approved',
                note: 'নিবন্ধন আবেদন অনুমোদনের সময় প্রাথমিক বিনিয়োগ',
              },
            ]
          : [];
      saveData({
        ...data,
        members: [...data.members, member],
        payments: [...data.payments, ...payment],
        memberRequests: data.memberRequests.map((item) =>
          item.id === request.id ? { ...item, status: 'approved', approvedAt: today(), createdUserId: member.userId } : item
        ),
      });
      return;
    }

    if (request.type === 'update') {
      saveData({
        ...data,
        members: data.members.map((member) =>
          member.id === request.memberId
            ? buildMemberFromPayload(request.payload, {
                id: member.id,
                userId: member.userId,
                status: member.status,
                joinedAt: member.joinedAt,
              })
            : member
        ),
        memberRequests: data.memberRequests.map((item) =>
          item.id === request.id ? { ...item, status: 'approved', approvedAt: today() } : item
        ),
      });
    }
  };

  const rejectMemberRequest = (request) => {
    if (!can('requests', 'approve')) return;
    saveData({
      ...data,
      memberRequests: data.memberRequests.map((item) =>
        item.id === request.id ? { ...item, status: 'rejected', rejectedAt: today() } : item
      ),
    });
  };

  // Delete handlers for various resources
  const deleteMember = (id) => {
    if (!can('members', 'delete')) return;
    if (!window.confirm('আপনি কি নিশ্চিত যে এই সদস্যটি মুছে ফেলতে চান? এই ক্রিয়া অবলম্বনে সম্পর্কিত পেমেন্ট এবং কমিটি-নির্ধারণও মুছে যাবে।')) return;
    saveData({
      ...data,
      members: data.members.filter((m) => m.id !== id),
      payments: data.payments.filter((p) => p.memberId !== id),
      committeeMembers: (data.committeeMembers || []).filter((c) => Number(c.memberId) !== id),
    });
    setViewingMemberId(null);
  };

  const deletePayment = (id) => {
    if (!can('payments', 'delete')) return;
    if (!window.confirm('পেমেন্ট মুছে ফেলতে চান?')) return;
    saveData({ ...data, payments: data.payments.filter((p) => p.id !== id) });
  };

  const approvePayment = (id) => {
    if (!can('payments', 'approve')) return;
    saveData({
      ...data,
      payments: data.payments.map((payment) =>
        payment.id === id
          ? { ...payment, status: 'approved', approvedAt: today(), receiptNo: payment.receiptNo || receiptNo(payment) }
          : payment
      ),
    });
  };

  const deleteExpense = (id) => {
    if (!can('expenses', 'delete')) return;
    if (!window.confirm('এই খরচের এন্ট্রি মুছে ফেলতে চান?')) return;
    saveData({ ...data, expenses: data.expenses.filter((e) => e.id !== id) });
  };

  const deleteLandProject = (id) => {
    if (!can('land', 'delete')) return;
    if (!window.confirm('প্রকল্প মুছে ফেলতে চান?')) return;
    saveData({ ...data, landProjects: data.landProjects.filter((p) => p.id !== id) });
  };

  const deleteCommitteeMember = (id) => {
    if (!can('committee', 'delete')) return;
    if (!window.confirm('কমিটি সদস্য মুছে ফেলতে চান?')) return;
    saveData({ ...data, committeeMembers: (data.committeeMembers || []).filter((c) => c.id !== id) });
  };

  const deleteCommitteeGroup = (committeeName) => {
    if (!can('committee', 'delete')) return;
    if (!window.confirm(`${committeeName} কমিটি মুছে ফেলতে চান?`)) return;
    const nextDetails = { ...(data.committeeDetails || {}) };
    delete nextDetails[committeeName];
    saveData({
      ...data,
      committeeMembers: (data.committeeMembers || []).filter((member) => (member.committeeName || defaultCommitteeName) !== committeeName),
      committeeDetails: nextDetails,
    });
  };

  const saveAdminUser = (event) => {
    event.preventDefault();
    if (editingAdminUserId ? !can('adminUsers', 'edit') : !can('adminUsers', 'add')) return;
    const adminUser = {
      id: editingAdminUserId || Date.now(),
      name: adminUserForm.name,
      userId: String(adminUserForm.userId || '').trim().toUpperCase(),
      password: adminUserForm.password,
      status: adminUserForm.status || 'active',
      permissions: adminUserForm.permissions || {},
      createdAt: adminUserForm.createdAt || today(),
    };
    saveData({
      ...data,
      adminUsers: editingAdminUserId
        ? (data.adminUsers || []).map((item) => (item.id === editingAdminUserId ? adminUser : item))
        : [adminUser, ...(data.adminUsers || [])],
    });
    setAdminUserForm(defaultAdminUserForm());
    setEditingAdminUserId(null);
  };

  const editAdminUser = (adminUser) => {
    if (!can('adminUsers', 'edit')) return;
    setEditingAdminUserId(adminUser.id);
    setAdminUserForm({
      ...adminUser,
      permissions: normalizeAdminPermissions(adminUser.permissions),
    });
    setActive('adminUsers');
  };

  const deleteAdminUser = (id) => {
    if (!can('adminUsers', 'delete')) return;
    if (!window.confirm('এই এডমিন ইউজার মুছে ফেলতে চান?')) return;
    saveData({ ...data, adminUsers: (data.adminUsers || []).filter((item) => item.id !== id) });
  };

  return (
    <Shell title="অ্যাডমিন প্যানেল" subtitle={`সদস্য, পেমেন্ট, খরচ ও প্রকল্প ব্যবস্থাপনা | ${syncStatusLabel(syncStatus)}`} navItems={nav} active={active} setActive={setActive} onLogout={onLogout}>
      {active === 'dashboard' && <AdminDashboard data={data} totals={totals} />}
      {active === 'members' && (
        <MembersTab
          data={data}
          form={memberForm}
          setForm={setMemberForm}
          onSubmit={saveMember}
          editingId={editingMemberId}
          onEdit={editMember}
          viewingMemberId={viewingMemberId}
          onView={(member) => setViewingMemberId(viewingMemberId === member.id ? null : member.id)}
          onCancel={() => {
            setMemberForm({});
            setEditingMemberId(null);
          }}
          onDeleteMember={deleteMember}
          permissions={permissions.members}
        />
      )}
      {active === 'requests' && <MemberRequestsTab data={data} onApprove={approveMemberRequest} onReject={rejectMemberRequest} permissions={permissions.requests} />}
      {active === 'payments' && (
        <PaymentsTab data={data} form={paymentForm} setForm={setPaymentForm} onSubmit={addPayment} onApprovePayment={approvePayment} onDeletePayment={deletePayment} permissions={permissions.payments} />
      )}
      {active === 'expenses' && (
        <ExpensesTab data={data} form={expenseForm} setForm={setExpenseForm} onSubmit={addExpense} onDeleteExpense={deleteExpense} permissions={permissions.expenses} />
      )}
      {active === 'reports' && <ReportsTab data={data} totals={totals} />}
      {active === 'land' && (
        <LandTab
          data={data}
          form={landForm}
          setForm={setLandForm}
          onSubmit={saveLandProject}
          editingId={editingLandId}
          onEdit={editLandProject}
          onDelete={deleteLandProject}
          permissions={permissions.land}
          onCancel={() => {
            setLandForm({ checks: 'খতিয়ান, নামজারি, দলিল, ভূমি উন্নয়ন কর, দখল অবস্থা' });
            setEditingLandId(null);
          }}
        />
      )}
      {active === 'committee' && (
        <CommitteeTab
          data={data}
          form={committeeForm}
          setForm={setCommitteeForm}
          onSubmit={saveCommitteeMember}
          onSaveDetails={saveCommitteeDetails}
          editingId={editingCommitteeId}
          onEdit={editCommitteeMember}
          onDeleteCommittee={deleteCommitteeMember}
          onDeleteCommitteeGroup={deleteCommitteeGroup}
          permissions={permissions.committee}
          onCancel={() => {
            setCommitteeForm({
              committeeName: defaultCommitteeName,
              position: 'সদস্য',
              termStart: today(),
              termEnd: today(),
              status: 'active',
            });
            setEditingCommitteeId(null);
          }}
        />
      )}
      {active === 'advice' && <AdminAdviceTab data={data} />}
  {active === 'notices' && (
    <NoticesTab
      data={data}
      form={noticeForm}
      setForm={setNoticeForm}
      onSubmit={addNotice}
      editingNoticeId={editingNoticeId}
      onEditNotice={editNotice}
      onDeleteNotice={deleteNotice}
      permissions={permissions.notices}
      onCancelEdit={() => {
        setEditingNoticeId(null);
        setNoticeForm({ date: today() });
      }}
    />
  )}
      {active === 'adminUsers' && (
        <AdminUsersTab
          data={data}
          form={adminUserForm}
          setForm={setAdminUserForm}
          onSubmit={saveAdminUser}
          editingId={editingAdminUserId}
          onEdit={editAdminUser}
          onDelete={deleteAdminUser}
          onCancel={() => {
            setAdminUserForm(defaultAdminUserForm());
            setEditingAdminUserId(null);
          }}
          permissions={permissions.adminUsers}
        />
      )}
    </Shell>
  );
}

function AdminDashboard({ data, totals }) {
  const activeMembers = data.members.filter((member) => member.status === 'active').length;
  const pendingRequests = data.memberRequests.filter((request) => request.status === 'pending');
  const approvedPayments = data.payments.filter((payment) => payment.status === 'approved');
  const pendingPayments = data.payments.filter((payment) => payment.status === 'pending');
  const monthlyPremiumTotal = approvedPayments
    .filter((payment) => payment.type === 'মাসিক চাঁদা')
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const committeeNames = Array.from(new Set((data.committeeMembers || []).map((member) => member.committeeName || defaultCommitteeName)));
  const latestPayments = [...data.payments].slice(0, 6);
  const latestExpenses = [...data.expenses].slice(0, 6);
  const latestRequests = [...data.memberRequests].slice(0, 5);
  const memberCapacity = `${data.members.length}/৪০ জন`;

  return (
    <div className="stack">
      <section className="admin-hero">
        <div>
          <span>সবর গার্ডেন অ্যাডমিন কন্ট্রোল</span>
          <h2>স্বচ্ছ হিসাব, সদস্য ব্যবস্থাপনা ও প্রকল্প নজরদারি</h2>
          <p>আজকের ব্যালেন্স {currency(totals.balance)} | সদস্য {memberCapacity} | অপেক্ষমান রিকোয়েস্ট {pendingRequests.length}টি</p>
        </div>
        <div className="admin-hero-badge">
          <Banknote size={34} />
          <strong>{currency(totals.deposits)}</strong>
          <span>মোট অনুমোদিত জমা</span>
        </div>
      </section>

      <div className="member-stat-grid">
        <ColorStatCard tone="green" icon={Users} label="মোট সদস্য" value={memberCapacity} />
        <ColorStatCard tone="blue" icon={ShieldCheck} label="সক্রিয় সদস্য" value={`${activeMembers} জন`} />
        <ColorStatCard tone="purple" icon={Banknote} label="বর্তমান ব্যালেন্স" value={currency(totals.balance)} />
        <ColorStatCard tone="amber" icon={Receipt} label="প্রকল্প খরচ" value={currency(totals.expenses)} />
        <ColorStatCard tone="green" icon={CreditCard} label="মাসিক চাঁদা" value={currency(monthlyPremiumTotal)} />
        <ColorStatCard tone="blue" icon={FileText} label="অপেক্ষমান রিকোয়েস্ট" value={`${pendingRequests.length}টি`} />
        <ColorStatCard tone="purple" icon={Landmark} label="জমি প্রকল্প" value={`${data.landProjects.length}টি`} />
        <ColorStatCard tone="amber" icon={UserRoundCog} label="কমিটি" value={`${committeeNames.length}টি`} />
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>আর্থিক সারাংশ</h3>
            <span>জমা, খরচ ও ব্যালেন্স</span>
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="অনুমোদিত পেমেন্ট" value={`${approvedPayments.length}টি`} />
            <Info label="অপেক্ষমান পেমেন্ট" value={`${pendingPayments.length}টি`} />
            <Info label="রিফান্ড" value={currency(totals.refunds)} />
            <Info label="মোট খরচ" value={currency(totals.expenses)} />
            <Info label="নিট ব্যালেন্স" value={currency(totals.balance)} />
            <Info label="ব্যাংক হিসাব" value="UCB - 7863241001126362" />
          </div>
        </section>

        <section className="panel">
          <div className="section-title">
            <h3>সংগঠন সারাংশ</h3>
            <span>সদস্য, কমিটি ও প্রকল্প</span>
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="সদস্য ধারণক্ষমতা" value={memberCapacity} />
            <Info label="সক্রিয় সদস্য" value={`${activeMembers} জন`} />
            <Info label="কমিটি" value={`${committeeNames.length}টি`} />
            <Info label="কমিটি সদস্য" value={`${data.committeeMembers.length} জন`} />
            <Info label="জমি প্রকল্প" value={`${data.landProjects.length}টি`} />
            <Info label="নোটিশ/রেজুলেশন" value={`${data.notices.length}টি`} />
          </div>
        </section>
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>সাম্প্রতিক পেমেন্ট</h3>
            <span>{latestPayments.length}টি রেকর্ড</span>
          </div>
          <DataTable
            headers={['তারিখ', 'সদস্য', 'ধরন', 'টাকা', 'স্ট্যাটাস']}
            rows={latestPayments.map((item) => [
              item.date,
              memberName(data, item.memberId),
              item.type,
              currency(item.amount),
              statusLabel(item.status),
            ])}
          />
        </section>

        <section className="panel">
          <div className="section-title">
            <h3>সাম্প্রতিক খরচ</h3>
            <span>{latestExpenses.length}টি রেকর্ড</span>
          </div>
          <DataTable
            headers={['তারিখ', 'কাজ', 'ক্যাটাগরি', 'টাকা']}
            rows={latestExpenses.map((item) => [item.date, item.title, item.category, currency(item.amount)])}
          />
        </section>
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>অপেক্ষমান রিকোয়েস্ট</h3>
            <span>নিবন্ধন/আপডেট অনুমোদন</span>
          </div>
          <DataTable
            headers={['তারিখ', 'ধরন', 'নাম', 'স্ট্যাটাস']}
            rows={latestRequests.map((request) => [
              request.submittedAt,
              request.type === 'registration' ? 'নতুন নিবন্ধন' : 'তথ্য আপডেট',
              request.payload?.name || memberName(data, request.memberId),
              statusLabel(request.status),
            ])}
          />
        </section>

        <section className="panel">
          <div className="section-title">
            <h3>জমি ও কমিটি অবস্থা</h3>
            <span>দ্রুত নজরদারি</span>
          </div>
          <DataTable
            headers={['ধরন', 'নাম', 'স্ট্যাটাস/সংখ্যা']}
            rows={[
              ...data.landProjects.slice(0, 4).map((project) => ['জমি প্রকল্প', project.name, project.status || '-']),
              ...committeeNames.slice(0, 4).map((name) => [
                'কমিটি',
                name,
                `${data.committeeMembers.filter((member) => (member.committeeName || defaultCommitteeName) === name).length} জন`,
              ]),
            ]}
          />
        </section>
      </div>
    </div>
  );
}

function MembersTab({ data, form, setForm, onSubmit, editingId, onEdit, viewingMemberId, onView, onCancel, onDeleteMember, permissions = {} }) {
  const viewingMember = data.members.find((member) => member.id === viewingMemberId);

  return (
    <div className="stack">
      {(editingId ? permissions.edit : permissions.add) && <section className="panel">
        <div className="section-title">
          <h3>{editingId ? 'সদস্য তথ্য এডিট' : 'নতুন সদস্য নিবন্ধন'}</h3>
          <span>বায়োডাটা ও নমিনি তথ্য</span>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <FileInput label="সদস্যের ছবি" name="photo" form={form} setForm={setForm} required={!editingId} />
          <Input label="পূর্ণ নাম" name="name" form={form} setForm={setForm} required />
          <Input label="মোবাইল নম্বর" name="mobile" form={form} setForm={setForm} required />
          <Input label="জাতীয় পরিচয়পত্র নম্বর" name="nid" form={form} setForm={setForm} required />
          <AttachmentInput label="NID / ডকুমেন্ট এটাচমেন্ট" name="attachment" form={form} setForm={setForm} required={!editingId} wide={false} />
          <Input label="প্রাথমিক বিনিয়োগ" name="investment" type="number" form={form} setForm={setForm} span="third" />
          <Input label="পেশা" name="profession" form={form} setForm={setForm} span="third" />
          <Input label="ইমেইল" name="email" type="email" form={form} setForm={setForm} span="third" />
          <Input label="পিতার নাম" name="father" form={form} setForm={setForm} required />
          <Input label="মাতার নাম" name="mother" form={form} setForm={setForm} required />
          <Input label="স্থায়ী ঠিকানা" name="permanentAddress" form={form} setForm={setForm} wide required />
          <Input label="বর্তমান ঠিকানা" name="currentAddress" form={form} setForm={setForm} wide required />
          <Input label="জরুরি যোগাযোগ" name="emergency" form={form} setForm={setForm} wide />
          <Input label="নমিনির নাম" name="nomineeName" form={form} setForm={setForm} required />
          <Input label="সম্পর্ক" name="nomineeRelation" form={form} setForm={setForm} required />
          <Input label="পিতা/স্বামী" name="nomineeFather" form={form} setForm={setForm} required />
          <Input label="নমিনির মোবাইল" name="nomineeMobile" form={form} setForm={setForm} required />
          <Input label="নমিনির NID" name="nomineeNid" form={form} setForm={setForm} required />
          <FileInput label="নমিনির ছবি" name="nomineePhoto" form={form} setForm={setForm} required={!editingId} />
          <Input label="নমিনির ঠিকানা" name="nomineeAddress" form={form} setForm={setForm} wide />
          <div className="form-action action-row">
            <button className="primary-button" type="submit">
              {editingId ? <Save size={18} /> : <Plus size={18} />}
              {editingId ? 'আপডেট করুন' : 'সদস্য যোগ করুন'}
            </button>
            {editingId && (
              <button className="secondary-button" type="button" onClick={onCancel}>
                বাতিল
              </button>
            )}
          </div>
        </form>
      </section>}

      <section className="panel">
        <div className="section-title">
          <h3>সদস্য তালিকা</h3>
          <span>{data.members.length} জন সদস্য</span>
        </div>
        <DataTable
          headers={['ছবি', 'আইডি', 'নাম', 'মোবাইল', 'প্রাথমিক বিনিয়োগ', 'অবস্থা', 'অ্যাকশন']}
          rows={data.members.map((item) => [
            <MemberThumb member={item} />,
            item.userId,
            item.name,
            item.mobile,
            currency(item.investment),
            statusLabel(item.status),
            <div className="action-row">
              <button className="secondary-button small-button" type="button" onClick={() => onView(item)}>
                <Eye size={15} /> ভিউ
              </button>
              {permissions.edit && <button className="secondary-button small-button" type="button" onClick={() => onEdit(item)}>
                <Pencil size={15} /> এডিট
              </button>}
              {permissions.delete && <button className="secondary-button small-button" type="button" onClick={() => onDeleteMember && onDeleteMember(item.id)}>
                <Trash2 size={15} /> ডিলিট
              </button>}
            </div>,
          ])}
        />
      </section>

      {viewingMember && <AdminMemberDetails data={data} member={viewingMember} />}
    </div>
  );
}

function AdminMemberDetails({ data, member }) {
  const payments = data.payments.filter((payment) => payment.memberId === member.id);
  const summary = memberSummary(payments);
  const committeeRole = data.committeeMembers.find((item) => Number(item.memberId) === member.id);

  return (
    <section className="panel member-details-panel">
      <div className="section-title">
        <h3>সদস্য বিস্তারিত</h3>
        <span>{member.userId}</span>
      </div>
      <div className="member-detail-head">
        <div className="avatar large-avatar">
          {member.photo ? <img src={member.photo} alt={member.name} /> : <User size={42} />}
        </div>
        <div>
          <h2>{member.name}</h2>
          <p>{member.mobile} | {statusLabel(member.status)}</p>
          <p>যোগদানের তারিখ: {member.joinedAt || '-'}</p>
        </div>
      </div>

      <div className="stat-grid compact-stats">
        <StatCard icon={Wallet} label="মোট জমা" value={currency(summary.deposits)} />
        <StatCard icon={Banknote} label="নিট ব্যালেন্স" value={currency(summary.balance)} />
        <StatCard icon={UserRoundCog} label="কমিটি পদ" value={committeeRole?.position || 'নেই'} />
      </div>

      <div className="divider" />
      <h3>বায়োডাটা</h3>
      <div className="info-grid">
        <Info label="সদস্য আইডি" value={member.userId} />
        <Info label="পূর্ণ নাম" value={member.name} />
        <Info label="পিতার নাম" value={member.father} />
        <Info label="মাতার নাম" value={member.mother} />
        <Info label="মোবাইল" value={member.mobile} />
        <Info label="NID" value={member.nid} />
        <Info label="ধর্ম" value={member.religion} />
        <Info label="পেশা" value={member.profession} />
        <Info label="ইমেইল" value={member.email} />
        <Info label="NID/ডকুমেন্ট এটাচমেন্ট" value={member.attachment?.name || '-'} />
        <Info label="প্রাথমিক বিনিয়োগ" value={currency(member.investment)} />
        <Info label="স্থায়ী ঠিকানা" value={member.permanentAddress} />
        <Info label="বর্তমান ঠিকানা" value={member.currentAddress} />
        <Info label="জরুরি যোগাযোগ" value={member.emergency} />
      </div>
      {documentUrl(member.attachment) && (
        <a className="attachment-link" href={documentUrl(member.attachment)} download={member.attachment.name || 'member-attachment'}>
          NID/ডকুমেন্ট ডাউনলোড
        </a>
      )}

      <div className="divider" />
      <h3>নমিনি তথ্য</h3>
      <div className="member-detail-head nominee-head">
        <div className="avatar">
          {member.nominee?.photo ? <img src={member.nominee.photo} alt={member.nominee.name || 'Nominee'} /> : <User size={32} />}
        </div>
        <div>
          <strong>{member.nominee?.name || '-'}</strong>
          <p>{member.nominee?.relation || '-'}</p>
        </div>
      </div>
      <div className="info-grid">
        <Info label="নমিনির নাম" value={member.nominee?.name} />
        <Info label="সম্পর্ক" value={member.nominee?.relation} />
        <Info label="পিতা/স্বামী" value={member.nominee?.father} />
        <Info label="মোবাইল" value={member.nominee?.mobile} />
        <Info label="NID" value={member.nominee?.nid} />
        <Info label="ঠিকানা" value={member.nominee?.address} />
      </div>

      <div className="divider" />
      <h3>পেমেন্ট হিস্টরি</h3>
      <DataTable
        headers={['তারিখ', 'ধরন', 'টাকা', 'মেথড', 'ট্রানজেকশন', 'স্ট্যাটাস']}
        rows={payments.map((payment) => [
          payment.date,
          payment.type,
          currency(payment.amount),
          payment.method,
          payment.trxId || '-',
          statusLabel(payment.status),
        ])}
      />
    </section>
  );
}

function PaymentsTab({ data, form, setForm, onSubmit, onApprovePayment, onDeletePayment, permissions = {} }) {
  const [receiptPayment, setReceiptPayment] = useState(null);
  const receiptMember = receiptPayment ? data.members.find((member) => member.id === Number(receiptPayment.memberId)) : null;

  return (
    <div className="stack">
      {permissions.add && <section className="panel">
        <div className="section-title">
          <h3>পেমেন্ট রেকর্ড</h3>
          <span>জমা, চাঁদা, লাভ বা রিফান্ড</span>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <label>
            সদস্য
            <select value={form.memberId || ''} onChange={(event) => setForm({ ...form, memberId: event.target.value })} required>
              <option value="">সদস্য নির্বাচন করুন</option>
              {data.members.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({item.userId})
                </option>
              ))}
            </select>
          </label>
          <label>
            ধরন
            <select value={form.type || 'মাসিক চাঁদা'} onChange={(event) => setForm({ ...form, type: event.target.value })}>
              <option>প্রাথমিক বিনিয়োগ</option>
              <option>মাসিক চাঁদা</option>
              <option>অতিরিক্ত বিনিয়োগ</option>
              <option>লাভ বণ্টন</option>
              <option>রিফান্ড</option>
              <option>অনলাইন পেমেন্ট অনুরোধ</option>
            </select>
          </label>
          <Input label="টাকার পরিমাণ" name="amount" type="number" form={form} setForm={setForm} required />
          <Input label="তারিখ" name="date" type="date" form={form} setForm={setForm} required />
          <Input label="মেথড" name="method" form={form} setForm={setForm} />
          <label>
            প্রকল্প
            <select value={form.projectId || ''} onChange={(event) => setForm({ ...form, projectId: event.target.value })}>
              <option value="">সাধারণ ফান্ড</option>
              {data.landProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <Input label="ট্রানজেকশন আইডি" name="trxId" form={form} setForm={setForm} />
          <label>
            স্ট্যাটাস
            <select value={form.status || 'approved'} onChange={(event) => setForm({ ...form, status: event.target.value })}>
              <option value="approved">অনুমোদিত</option>
              <option value="pending">অপেক্ষমান</option>
              <option value="rejected">বাতিল</option>
            </select>
          </label>
          <Input label="বিবরণ" name="note" form={form} setForm={setForm} wide />
          <button className="primary-button form-action" type="submit">
            <Plus size={18} /> পেমেন্ট যোগ করুন
          </button>
        </form>
      </section>}
      <section className="panel">
        <DataTable
          headers={['তারিখ', 'সদস্য', 'ধরন', 'টাকা', 'মেথড', 'প্রকল্প', 'ট্রানজেকশন', 'স্ট্যাটাস', 'অ্যাকশন']}
          rows={data.payments.map((item) => [
            item.date,
            memberName(data, item.memberId),
            item.type,
            currency(item.amount),
            item.method,
            projectName(data, item.projectId),
            item.trxId || '-',
            statusLabel(item.status),
            <div className="action-row">
              {item.status === 'approved' && (
                <button className="secondary-button small-button" type="button" onClick={() => setReceiptPayment(item)}>
                  <Receipt size={14} /> রিসিট
                </button>
              )}
              {item.status === 'pending' && permissions.approve && (
                <button className="primary-button small-button" type="button" onClick={() => onApprovePayment && onApprovePayment(item.id)}>
                  <BadgeCheck size={14} /> এপ্রুভ
                </button>
              )}
              {permissions.delete && (
              <button className="secondary-button small-button" type="button" onClick={() => onDeletePayment && onDeletePayment(item.id)}>
                <Trash2 size={14} /> ডিলিট
              </button>
              )}
            </div>,
          ])}
        />
      </section>
      {receiptPayment && receiptMember && (
        <PaymentReceiptModal
          payment={receiptPayment}
          member={receiptMember}
          data={data}
          onClose={() => setReceiptPayment(null)}
        />
      )}
    </div>
  );
}

function MemberRequestsTab({ data, onApprove, onReject, permissions = {} }) {
  return (
    <div className="stack">
      <section className="panel">
        <div className="section-title">
          <h3>সদস্য নিবন্ধন ও আপডেট রিকোয়েস্ট</h3>
          <span>{data.memberRequests.filter((item) => item.status === 'pending').length}টি অপেক্ষমান</span>
        </div>
        <DataTable
          headers={['তারিখ', 'ধরন', 'সদস্য', 'মোবাইল', 'স্ট্যাটাস', 'অ্যাকশন']}
          rows={data.memberRequests.map((request) => [
            request.submittedAt,
            request.type === 'registration' ? 'নতুন নিবন্ধন' : 'তথ্য আপডেট',
            request.payload?.name || memberName(data, request.memberId),
            request.payload?.mobile || '-',
            statusLabel(request.status),
            request.status === 'pending' && permissions.approve ? (
              <div className="action-row">
                <button className="primary-button small-button" type="button" onClick={() => onApprove(request)}>
                  অনুমোদন
                </button>
                <button className="secondary-button small-button" type="button" onClick={() => onReject(request)}>
                  বাতিল
                </button>
              </div>
            ) : (
              request.createdUserId || '-'
            ),
          ])}
        />
      </section>
      {data.memberRequests.map((request) => (
        <section className="panel" key={`detail-${request.id}`}>
          <div className="section-title">
            <h3>{request.payload?.name || memberName(data, request.memberId)}</h3>
            <span>{request.type === 'registration' ? 'নতুন সদস্য আবেদন' : 'আপডেট আবেদন'}</span>
          </div>
          <div className="profile-header">
            <div className="avatar">{request.payload?.photo ? <img src={request.payload.photo} alt={request.payload.name} /> : <User size={34} />}</div>
            <div>
              <strong>{request.payload?.name || '-'}</strong>
              <p>{request.payload?.mobile || '-'}</p>
            </div>
          </div>
          <div className="info-grid">
            <Info label="পিতার নাম" value={request.payload?.father} />
            <Info label="মাতার নাম" value={request.payload?.mother} />
            <Info label="NID" value={request.payload?.nid} />
            <Info label="NID/ডকুমেন্ট এটাচমেন্ট" value={request.payload?.attachment?.name || '-'} />
            <Info label="পেশা" value={request.payload?.profession} />
            <Info label="বর্তমান ঠিকানা" value={request.payload?.currentAddress} />
            <Info label="স্থায়ী ঠিকানা" value={request.payload?.permanentAddress} />
            <Info label="নমিনি" value={request.payload?.nomineeName} />
            <Info label="নমিনি মোবাইল" value={request.payload?.nomineeMobile} />
            <Info label="নমিনি NID" value={request.payload?.nomineeNid} />
          </div>
          {documentUrl(request.payload?.attachment) && (
            <a className="attachment-link" href={documentUrl(request.payload.attachment)} download={request.payload.attachment.name || 'member-attachment'}>
              NID/ডকুমেন্ট ডাউনলোড
            </a>
          )}
          {request.payload?.nomineePhoto && (
            <div className="nominee-photo-block">
              <span>নমিনির ছবি</span>
              <img src={request.payload.nomineePhoto} alt="Nominee" />
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function ExpensesTab({ data, form, setForm, onSubmit, onDeleteExpense, permissions = {} }) {
  return (
    <div className="stack">
      {permissions.add && <section className="panel">
        <div className="section-title">
          <h3>অ্যাডমিন/প্রকল্প খরচ</h3>
          <span>কোন কাজে টাকা ব্যবহার হলো তা সংরক্ষণ</span>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <Input label="খরচের শিরোনাম" name="title" form={form} setForm={setForm} required />
          <Input label="ক্যাটাগরি" name="category" form={form} setForm={setForm} />
          <Input label="টাকার পরিমাণ" name="amount" type="number" form={form} setForm={setForm} required />
          <Input label="তারিখ" name="date" type="date" form={form} setForm={setForm} required />
          <Input label="মেথড" name="method" form={form} setForm={setForm} />
          <label>
            প্রকল্প
            <select value={form.projectId || ''} onChange={(event) => setForm({ ...form, projectId: event.target.value })}>
              <option value="">সাধারণ খরচ</option>
              {data.landProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
          <Input label="অনুমোদনকারী" name="approvedBy" form={form} setForm={setForm} />
          <Input label="বিবরণ" name="note" form={form} setForm={setForm} wide />
          <button className="primary-button form-action" type="submit">
            <Plus size={18} /> খরচ যোগ করুন
          </button>
        </form>
      </section>}
      <section className="panel">
        <DataTable
          headers={['তারিখ', 'কাজ', 'ক্যাটাগরি', 'টাকা', 'প্রকল্প', 'অনুমোদন', 'বিবরণ', 'অ্যাকশন']}
          rows={data.expenses.map((item) => [
            item.date,
            item.title,
            item.category,
            currency(item.amount),
            projectName(data, item.projectId),
            item.approvedBy,
            item.note,
            <div className="action-row">
              {permissions.delete && (
              <button className="secondary-button small-button" type="button" onClick={() => onDeleteExpense && onDeleteExpense(item.id)}>
                <Trash2 size={14} /> ডিলিট
              </button>
              )}
            </div>,
          ])}
        />
      </section>
    </div>
  );
}

function ReportsTab({ data, totals }) {
  const [period, setPeriod] = useState('monthly');
  const report = buildReport(data, period);
  const paymentTypeRows = Object.entries(report.byPaymentType).map(([type, amount]) => [type, currency(amount)]);
  const expenseCategoryRows = Object.entries(report.byExpenseCategory).map(([category, amount]) => [category, currency(amount)]);
  const projectRows = buildProjectReport(data, period);

  return (
    <div className="stack">
      <section className="admin-hero report-hero">
        <div>
          <span>রিপোর্ট সেন্টার</span>
          <h2>দৈনিক, সাপ্তাহিক, মাসিক ও বাৎসরিক আয়-ব্যয় হিসাব</h2>
          <p>নির্বাচিত রিপোর্টে আয় {currency(report.income)} | ব্যয় {currency(report.expense)} | ব্যালেন্স {currency(report.balance)}</p>
        </div>
        <div className="report-filter">
          <label>
            রিপোর্ট ধরন
            <select value={period} onChange={(event) => setPeriod(event.target.value)}>
              <option value="daily">দৈনিক</option>
              <option value="weekly">সাপ্তাহিক</option>
              <option value="monthly">মাসিক</option>
              <option value="yearly">বাৎসরিক</option>
              <option value="all">সর্বমোট</option>
            </select>
          </label>
        </div>
      </section>

      <div className="member-stat-grid">
        <ColorStatCard tone="green" icon={Wallet} label="রিপোর্ট আয়" value={currency(report.income)} />
        <ColorStatCard tone="amber" icon={Receipt} label="রিপোর্ট ব্যয়" value={currency(report.expense)} />
        <ColorStatCard tone="purple" icon={Banknote} label="রিপোর্ট ব্যালেন্স" value={currency(report.balance)} />
        <ColorStatCard tone="blue" icon={FileText} label="লেনদেন সংখ্যা" value={`${report.paymentCount + report.expenseCount}টি`} />
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>সামগ্রিক আর্থিক রিপোর্ট</h3>
            <span>সকল সময়ের হিসাব</span>
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="মোট আয়" value={currency(totals.deposits)} />
            <Info label="মোট ব্যয়" value={currency(totals.expenses)} />
            <Info label="রিফান্ড" value={currency(totals.refunds)} />
            <Info label="বর্তমান ব্যালেন্স" value={currency(totals.balance)} />
          </div>
        </section>

        <section className="panel">
          <div className="section-title">
            <h3>সদস্য ও প্রকল্প রিপোর্ট</h3>
            <span>সংগঠনভিত্তিক সারাংশ</span>
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="মোট সদস্য" value={`${data.members.length} জন`} />
            <Info label="সক্রিয় সদস্য" value={`${data.members.filter((member) => member.status === 'active').length} জন`} />
            <Info label="জমি প্রকল্প" value={`${data.landProjects.length}টি`} />
            <Info label="কমিটি সদস্য" value={`${data.committeeMembers.length} জন`} />
          </div>
        </section>
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>আয়ের ধরনভিত্তিক রিপোর্ট</h3>
            <span>{periodLabel(period)}</span>
          </div>
          <DataTable headers={['আয়ের ধরন', 'টাকা']} rows={paymentTypeRows} />
        </section>

        <section className="panel">
          <div className="section-title">
            <h3>ব্যয়ের ক্যাটাগরি রিপোর্ট</h3>
            <span>{periodLabel(period)}</span>
          </div>
          <DataTable headers={['ব্যয়ের ধরন', 'টাকা']} rows={expenseCategoryRows} />
        </section>
      </div>

      <section className="panel">
        <div className="section-title">
          <h3>প্রকল্পভিত্তিক আলাদা রিপোর্ট</h3>
          <span>{periodLabel(period)} অনুযায়ী প্রতিটি প্রকল্পের আয়, ব্যয়, ব্যালেন্স ও ডকুমেন্ট</span>
        </div>
        <DataTable
          headers={['প্রকল্প', 'স্ট্যাটাস', 'প্রাক্কলিত বাজেট', 'আয়', 'ব্যয়', 'নিট ব্যালেন্স', 'ডকুমেন্ট']}
          rows={projectRows.map((project) => [
            project.name,
            project.status || '-',
            currency(project.budget),
            currency(project.income),
            currency(project.expense),
            currency(project.balance),
            `${project.documentCount}টি`,
          ])}
        />
      </section>

      <section className="panel">
        <div className="section-title">
          <h3>রিপোর্ট লেনদেন তালিকা</h3>
          <span>আয় ও ব্যয় একসাথে</span>
        </div>
        <DataTable
          headers={['তারিখ', 'ধরন', 'বিবরণ', 'আয়', 'ব্যয়']}
          rows={report.rows.map((row) => [row.date, row.kind, row.description, row.income ? currency(row.income) : '-', row.expense ? currency(row.expense) : '-'])}
        />
      </section>
    </div>
  );
}

function LandTab({ data, form, setForm, onSubmit, editingId, onEdit, onDelete, onCancel, permissions = {} }) {
  const [viewingProjectId, setViewingProjectId] = useState(null);
  const viewingProject = data.landProjects.find((project) => project.id === viewingProjectId);
  const viewingFinancial = viewingProject ? projectFinancialSummary(data, viewingProject.id) : null;

  return (
    <div className="stack">
      {(editingId ? permissions.edit : permissions.add) && <section className="panel">
        <div className="section-title">
          <h3>{editingId ? 'জমি প্রকল্প এডিট করুন' : 'নতুন জমি প্রকল্প যোগ করুন'}</h3>
          <span>জমির যাচাই, বাজেট, দলিল ও বর্তমান অবস্থা</span>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <Input label="প্রকল্পের নাম" name="name" form={form} setForm={setForm} required />
          <Input label="অবস্থান" name="location" form={form} setForm={setForm} required />
          <Input label="জমির আকার" name="size" form={form} setForm={setForm} />
          <Input label="স্ট্যাটাস" name="status" form={form} setForm={setForm} />
          <Input label="প্রাক্কলিত বাজেট" name="budget" type="number" form={form} setForm={setForm} />
          <Input label="খতিয়ান নম্বর" name="khatian" form={form} setForm={setForm} />
          <Input label="দলিল নম্বর" name="deed" form={form} setForm={setForm} />
          <Input label="নামজারি/মিউটেশন" name="mutation" form={form} setForm={setForm} />
          <Input label="যাচাই আইটেম (কমা দিয়ে লিখুন)" name="checks" form={form} setForm={setForm} wide />
          <Input label="বিবরণ" name="note" form={form} setForm={setForm} wide />
          <ProjectDocumentsInput label="প্রকল্প ডকুমেন্ট এটাচমেন্ট" form={form} setForm={setForm} />
          <div className="form-action action-row">
            <button className="primary-button" type="submit">
              {editingId ? <Save size={18} /> : <Plus size={18} />}
              {editingId ? 'আপডেট করুন' : 'প্রকল্প যোগ করুন'}
            </button>
            {editingId && (
              <button className="secondary-button" type="button" onClick={onCancel}>
                বাতিল
              </button>
            )}
          </div>
        </form>
      </section>}

      <div className="project-grid">
        {data.landProjects.map((project) => (
          <article className="panel project-card" key={project.id}>
            <div className="card-topline">
              <Landmark size={32} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="secondary-button" type="button" onClick={() => setViewingProjectId(project.id)}>
                  <Eye size={16} /> ভিউ
                </button>
                {permissions.edit && <button className="secondary-button" type="button" onClick={() => onEdit(project)}>
                  <Pencil size={16} /> এডিট
                </button>}
                {permissions.delete && <button className="secondary-button" type="button" onClick={() => onDelete && onDelete(project.id)}>
                  <Trash2 size={16} /> ডিলিট
                </button>}
              </div>
            </div>
            <h3>{project.name}</h3>
            <p>
              <MapPin size={16} /> {project.location}
            </p>
            <div className="info-grid">
              <Info label="আকার" value={project.size} />
              <Info label="স্ট্যাটাস" value={project.status} />
              <Info label="প্রাক্কলিত বাজেট" value={currency(project.budget)} />
              <Info label="খতিয়ান" value={project.khatian || '-'} />
              <Info label="দলিল" value={project.deed || '-'} />
              <Info label="নামজারি" value={project.mutation || '-'} />
            </div>
            {project.note && <p className="muted-text">{project.note}</p>}
            <div className="document-count">
              <FileText size={16} /> {(project.documents || []).length}টি ডকুমেন্ট
            </div>
            <div className="check-list">
              {(project.checks || []).map((item) => (
                <span key={item}>
                  <BadgeCheck size={16} /> {item}
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>

      {viewingProject && (
        <section className="panel project-detail-panel">
          <div className="section-title">
            <div>
              <h3>{viewingProject.name}</h3>
              <span>প্রকল্পের পূর্ণ তথ্য, হিসাব ও সংরক্ষিত ডকুমেন্ট</span>
            </div>
            <button className="secondary-button" type="button" onClick={() => setViewingProjectId(null)}>
              <X size={16} /> বন্ধ
            </button>
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="অবস্থান" value={viewingProject.location} />
            <Info label="জমির আকার" value={viewingProject.size} />
            <Info label="স্ট্যাটাস" value={viewingProject.status} />
            <Info label="প্রাক্কলিত বাজেট" value={currency(viewingProject.budget)} />
            <Info label="খতিয়ান নম্বর" value={viewingProject.khatian || '-'} />
            <Info label="দলিল নম্বর" value={viewingProject.deed || '-'} />
            <Info label="নামজারি/মিউটেশন" value={viewingProject.mutation || '-'} />
            <Info label="ডকুমেন্ট সংখ্যা" value={`${(viewingProject.documents || []).length}টি`} />
            <Info label="প্রকল্প আয়" value={currency(viewingFinancial.income)} />
            <Info label="প্রকল্প ব্যয়" value={currency(viewingFinancial.expense)} />
            <Info label="নিট ব্যালেন্স" value={currency(viewingFinancial.balance)} />
          </div>
          {viewingProject.note && <p className="muted-text project-note">{viewingProject.note}</p>}
          <div className="check-list">
            {(viewingProject.checks || []).map((item) => (
              <span key={item}>
                <BadgeCheck size={16} /> {item}
              </span>
            ))}
          </div>
          <ProjectDocumentList documents={viewingProject.documents || []} />
        </section>
      )}
    </div>
  );
}

function CommitteeTab({ data, form, setForm, onSubmit, onSaveDetails, editingId, onEdit, onDeleteCommittee, onDeleteCommitteeGroup, onCancel, permissions = {} }) {
  const committeeNames = Array.from(
    new Set([
      ...(data.committeeMembers || []).map((member) => member.committeeName || defaultCommitteeName),
      form.committeeName || defaultCommitteeName,
    ])
  );
  const groupedCommittees = committeeNames
    .map((committeeName) => ({
      committeeName,
      members: data.committeeMembers.filter((member) => (member.committeeName || defaultCommitteeName) === committeeName),
    }))
    .filter((group) => group.members.length > 0);

  return (
    <div className="stack">
      {(editingId ? permissions.edit : permissions.add) && <section className="panel">
        <div className="section-title">
          <h3>{editingId ? 'কমিটি সদস্য এডিট করুন' : 'নতুন কমিটি সদস্য যোগ করুন'}</h3>
          <span>পদবি, মেয়াদ ও যোগাযোগ তথ্য</span>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <label>
            কমিটির নাম
            <input
              list="committee-name-options"
              value={form.committeeName || defaultCommitteeName}
              onChange={(event) => setForm({ ...form, committeeName: event.target.value })}
              required
            />
            <datalist id="committee-name-options">
              {committeeNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>
          <label>
            সদস্য নির্বাচন
            <select
              value={form.memberId || ''}
              onChange={(event) => {
                const selectedMember = data.members.find((item) => item.id === Number(event.target.value));
                setForm({
                  ...form,
                  memberId: event.target.value,
                  name: selectedMember?.name || '',
                  mobile: selectedMember?.mobile || '',
                  userId: selectedMember?.userId || '',
                  photo: selectedMember?.photo || '',
                  nid: selectedMember?.nid || '',
                  address: selectedMember?.currentAddress || selectedMember?.permanentAddress || '',
                });
              }}
              required
            >
              <option value="">সদস্য নির্বাচন করুন</option>
              {data.members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name} ({member.userId})
                </option>
              ))}
            </select>
          </label>
          <label>
            পদবি
            <select value={form.position || 'সদস্য'} onChange={(event) => setForm({ ...form, position: event.target.value })} required>
              {committeePositions.map((position) => (
                <option key={position} value={position}>
                  {position}
                </option>
              ))}
            </select>
          </label>
          <Input label="নাম" name="name" form={form} setForm={setForm} required />
          <Input label="মোবাইল" name="mobile" form={form} setForm={setForm} />
          <Input label="সদস্য আইডি" name="userId" form={form} setForm={setForm} />
          <Input label="মেয়াদ শুরু" name="termStart" type="date" form={form} setForm={setForm} />
          <Input label="মেয়াদ শেষ" name="termEnd" type="date" form={form} setForm={setForm} />
          <label>
            স্ট্যাটাস
            <select value={form.status || 'active'} onChange={(event) => setForm({ ...form, status: event.target.value })}>
              <option value="active">সক্রিয়</option>
              <option value="inactive">নিষ্ক্রিয়</option>
            </select>
          </label>
          <Input label="নোট" name="note" form={form} setForm={setForm} wide />
          <div className="form-action action-row">
            <button className="primary-button" type="submit">
              {editingId ? <Save size={18} /> : <Plus size={18} />}
              {editingId ? 'আপডেট করুন' : 'কমিটি সদস্য যোগ করুন'}
            </button>
            {editingId && (
              <button className="secondary-button" type="button" onClick={onCancel}>
                বাতিল
              </button>
            )}
          </div>
        </form>
      </section>}

      {groupedCommittees.map((group) => (
        <section className="panel committee-section" key={group.committeeName}>
          <div className="section-title">
            <h3>{group.committeeName}</h3>
            <div className="action-row">
              <span>{group.members.length} জন সদস্য</span>
              {permissions.delete && (
                <button className="secondary-button small-button danger" type="button" onClick={() => onDeleteCommitteeGroup(group.committeeName)}>
                  <Trash2 size={14} /> কমিটি ডিলিট
                </button>
              )}
            </div>
          </div>
          <CommitteeDetailsEditor
            committeeName={group.committeeName}
            details={(data.committeeDetails || {})[group.committeeName]}
            onSave={onSaveDetails}
          />
          <div className="committee-grid">
            {group.members.map((member) => (
              <div className="committee-card" key={member.id}>
                <div className="card-topline">
                  <MemberThumb member={member} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    {permissions.edit && <button className="secondary-button small-button" type="button" onClick={() => onEdit(member)}>
                      <Pencil size={15} /> এডিট
                    </button>}
                    {permissions.delete && <button className="secondary-button small-button" type="button" onClick={() => onDeleteCommittee && onDeleteCommittee(member.id)}>
                      <Trash2 size={15} /> ডিলিট
                    </button>}
                  </div>
                </div>
                <strong>{member.position}</strong>
                <span>{member.name}</span>
                <small>{member.userId || 'সদস্য আইডি নেই'}</small>
                <small>{member.mobile || 'মোবাইল নেই'}</small>
                <small>{member.address || 'ঠিকানা নেই'}</small>
                <small>{member.termStart} - {member.termEnd}</small>
                <small>{statusLabel(member.status)}</small>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function CommitteeDetailsEditor({ committeeName, details, onSave }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(
    details || {
      work: '',
      policy: '',
      duties: '',
      authority: '',
    }
  );

  const update = (field, value) => setForm({ ...form, [field]: value });

  return (
    <div className="committee-details-box">
      <div className="card-topline">
        <strong>কমিটি ভিউ ডিটেইলস</strong>
        <button className="secondary-button small-button" type="button" onClick={() => setOpen(!open)}>
          {open ? 'বন্ধ করুন' : 'ভিউ / আপডেট'}
        </button>
      </div>
      {!open && (
        <div className="committee-detail-summary">
          <Info label="কাজ" value={form.work || 'এখনও লেখা হয়নি'} />
          <Info label="পলিসি" value={form.policy || 'এখনও লেখা হয়নি'} />
          <Info label="দায়িত্ব" value={form.duties || 'এখনও লেখা হয়নি'} />
        </div>
      )}
      {open && (
        <form
          className="form-grid committee-detail-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(committeeName, form);
            setOpen(false);
          }}
        >
          <TextArea label="কমিটির কাজ" name="work" value={form.work} onChange={update} />
          <TextArea label="পলিসি / নীতিমালা" name="policy" value={form.policy} onChange={update} />
          <TextArea label="দায়িত্ব" name="duties" value={form.duties} onChange={update} />
          <TextArea label="ক্ষমতা / বিশেষ শর্ত" name="authority" value={form.authority} onChange={update} />
          <button className="primary-button form-action" type="submit">
            <Save size={18} /> ডিটেইলস আপডেট করুন
          </button>
        </form>
      )}
    </div>
  );
}

function NoticesTab({ data, form, setForm, onSubmit, editingNoticeId, onEditNotice, onDeleteNotice, onCancelEdit, permissions = {} }) {
  return (
    <div className="stack">
      {(editingNoticeId ? permissions.edit : permissions.add) && <section className="panel">
        <div className="section-title">
          <h3>নোটিশ ও রেজুলেশন</h3>
          <span>সভার সিদ্ধান্ত লিখিতভাবে সংরক্ষণ</span>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <Input label="শিরোনাম" name="title" form={form} setForm={setForm} required />
          <Input label="তারিখ" name="date" type="date" form={form} setForm={setForm} required />
          <Input label="বিবরণ" name="body" form={form} setForm={setForm} wide required />
          <div className="form-actions">
            <button className="primary-button form-action" type="submit">
              {editingNoticeId ? (
                <><Save size={18} /> আপডেট করুন</>
              ) : (
                <><Plus size={18} /> সংরক্ষণ করুন</>
              )}
            </button>
            {editingNoticeId && (
              <button
                type="button"
                className="secondary-button form-action"
                onClick={onCancelEdit}
              >
                বাতিল
              </button>
            )}
          </div>
        </form>
      </section>}
      {data.notices.map((notice) => (
        <section className="panel notice" key={notice.id}>
          <div className="notice-header flex items-center justify-between gap-3 mb-3">
            <div>
              <span>{notice.date}</span>
              <h3>{notice.title}</h3>
            </div>
            <div className="flex gap-2">
              {permissions.edit && <button
                type="button"
                className="secondary-button"
                onClick={() => onEditNotice(notice)}
              >
                <Pencil size={16} /> এডিট
              </button>}
              {permissions.delete && <button
                type="button"
                className="secondary-button danger"
                onClick={() => onDeleteNotice(notice.id)}
              >
                <Trash2 size={16} /> ডিলেট
              </button>}
            </div>
          </div>
          <p>{notice.body}</p>
        </section>
      ))}
    </div>
  );
}

function MemberAdviceTab({ data, form, setForm, onSubmit, message, member }) {
  return (
    <div className="stack">
      <section className="panel">
        <div className="section-title">
          <h3>পরামর্শ দিন</h3>
          <span>আপনার মতামত বা পরামর্শ এখানে লিখে পাঠাতে পারেন</span>
        </div>
        <form className="form-grid" onSubmit={onSubmit}>
          <Input label="শিরোনাম" name="title" form={form} setForm={setForm} required />
          <Input label="তারিখ" name="date" type="date" form={form} setForm={setForm} required />
          <Input label="পরামর্শ" name="advice" form={form} setForm={setForm} wide required />
          <button className="primary-button form-action" type="submit">
            <Plus size={18} /> পাঠান
          </button>
        </form>
        {message && <div className="success-box mt-4">{message}</div>}
      </section>
      <section className="panel">
        <div className="section-title">
          <h3>সমস্ত পরামর্শ</h3>
          <span>সকল সদস্য এবং অ্যাডমিন এটি দেখতে পারবেন</span>
        </div>
        {data.advice.length ? (
          data.advice.map((item) => (
            <section className="panel notice" key={item.id}>
              <div className="notice-header flex items-center justify-between gap-3 mb-3">
                <div>
                  <span>{item.date}</span>
                  <h3>{item.title}</h3>
                  <p className="text-sm text-gray-500">সদস্য: {item.memberName}</p>
                </div>
              </div>
              <p>{item.advice}</p>
            </section>
          ))
        ) : (
          <div className="empty-box">কোন পরামর্শ এখনও দেয়া হয়নি</div>
        )}
      </section>
    </div>
  );
}

function AdminAdviceTab({ data }) {
  return (
    <div className="stack">
      <section className="panel">
        <div className="section-title">
          <h3>সদস্য পরামর্শ</h3>
          <span>সকল সদস্যের পরামর্শ দেখুন</span>
        </div>
        {data.advice.length ? (
          data.advice.map((item) => (
            <section className="panel notice" key={item.id}>
              <div className="notice-header flex items-center justify-between gap-3 mb-3">
                <div>
                  <span>{item.date}</span>
                  <h3>{item.title}</h3>
                  <p className="text-sm text-gray-500">সদস্য: {item.memberName}</p>
                </div>
              </div>
              <p>{item.advice}</p>
            </section>
          ))
        ) : (
          <div className="empty-box">কোন সদস্য পরামর্শ পাওয়া যায়নি</div>
        )}
      </section>
    </div>
  );
}

function AdminUsersTab({ data, form, setForm, onSubmit, editingId, onEdit, onDelete, onCancel, permissions = {} }) {
  const updatePermission = (moduleId, action, checked) => {
    setForm({
      ...form,
      permissions: {
        ...(form.permissions || {}),
        [moduleId]: {
          ...((form.permissions || {})[moduleId] || {}),
          [action]: checked,
        },
      },
    });
  };

  return (
    <div className="stack">
      {(editingId ? permissions.edit : permissions.add) && (
        <section className="panel">
          <div className="section-title">
            <h3>{editingId ? 'এডমিন ইউজার এডিট' : 'নতুন এডমিন ইউজার যোগ করুন'}</h3>
            <span>যে permission দেবেন, user শুধু সেটুকুই দেখতে/করতে পারবে</span>
          </div>
          <form className="form-grid" onSubmit={onSubmit}>
            <Input label="নাম" name="name" form={form} setForm={setForm} required />
            <Input label="ইউজার আইডি" name="userId" form={form} setForm={setForm} required />
            <Input label="পাসওয়ার্ড" name="password" form={form} setForm={setForm} required />
            <label>
              স্ট্যাটাস
              <select value={form.status || 'active'} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                <option value="active">সক্রিয়</option>
                <option value="inactive">নিষ্ক্রিয়</option>
              </select>
            </label>
            <div className="wide permission-editor">
              {adminModules.map((module) => (
                <div className="permission-row" key={module.id}>
                  <strong>{module.label}</strong>
                  <div>
                    {module.actions.map((action) => (
                      <label className="permission-check" key={`${module.id}-${action}`}>
                        <input
                          type="checkbox"
                          checked={Boolean(form.permissions?.[module.id]?.[action])}
                          onChange={(event) => updatePermission(module.id, action, event.target.checked)}
                        />
                        {actionLabels[action]}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="form-action action-row">
              <button className="primary-button" type="submit">
                <Save size={18} /> {editingId ? 'আপডেট করুন' : 'ইউজার যোগ করুন'}
              </button>
              {editingId && (
                <button className="secondary-button" type="button" onClick={onCancel}>
                  বাতিল
                </button>
              )}
            </div>
          </form>
        </section>
      )}

      <section className="panel">
        <div className="section-title">
          <h3>এডমিন ইউজার তালিকা</h3>
          <span>Super Admin: ADMIN / admin123 সব access পাবে</span>
        </div>
        <DataTable
          headers={['নাম', 'ইউজার আইডি', 'স্ট্যাটাস', 'পারমিশন', 'অ্যাকশন']}
          rows={(data.adminUsers || []).map((user) => [
            user.name,
            user.userId,
            user.status === 'inactive' ? 'নিষ্ক্রিয়' : 'সক্রিয়',
            permissionSummary(user.permissions),
            <div className="action-row">
              {permissions.edit && (
                <button className="secondary-button small-button" type="button" onClick={() => onEdit(user)}>
                  <Pencil size={14} /> এডিট
                </button>
              )}
              {permissions.delete && (
                <button className="secondary-button small-button" type="button" onClick={() => onDelete(user.id)}>
                  <Trash2 size={14} /> ডিলিট
                </button>
              )}
            </div>,
          ])}
        />
      </section>
    </div>
  );
}

function MemberPortal({ data, saveData, member, syncStatus, onLogout }) {
  const [active, setActive] = useState(() => { try { return localStorage.getItem(ACTIVE_KEY) || 'dashboard'; } catch { return 'dashboard'; } });
  React.useEffect(() => { try { localStorage.setItem(ACTIVE_KEY, active); } catch {} }, [active]);
  const [updateForm, setUpdateForm] = useState(memberToForm(member));
  const [updateMessage, setUpdateMessage] = useState('');
  const [adviceForm, setAdviceForm] = useState({ title: '', advice: '', date: today() });
  const [adviceMessage, setAdviceMessage] = useState('');
  const [payForm, setPayForm] = useState({
    amount: '',
    method: 'bKash',
    paymentFor: 'মাসিক চাঁদা',
    projectId: '',
    date: today(),
  });
  const memberPayments = data.payments.filter((item) => item.memberId === member.id);
  const summary = memberSummary(memberPayments);
  const pendingMemberRequests = data.memberRequests.filter((request) => request.memberId === member.id && request.status === 'pending');
  const nav = [
    { id: 'dashboard', label: 'ড্যাশবোর্ড', icon: LayoutDashboard },
    { id: 'organization', label: 'সংগঠন', icon: Users },
    { id: 'profile', label: 'বায়োডাটা', icon: User },
    { id: 'update', label: 'তথ্য আপডেট', icon: Pencil },
    { id: 'payments', label: 'পেমেন্ট', icon: CreditCard },
    { id: 'statement', label: 'স্টেটমেন্ট', icon: FileText },
    { id: 'projects', label: 'প্রকল্প', icon: Landmark },
    { id: 'advice', label: 'পরামর্শ', icon: MessageCircle },
    { id: 'notice', label: 'নোটিশ', icon: CalendarDays },
  ];

  const requestPayment = async (event) => {
    event.preventDefault();
    let gatewayResult = null;
    if (isApiEnabled) {
      try {
        gatewayResult = await startGatewayPayment({
          provider: APP_CONFIG.paymentProvider || payForm.method,
          memberId: member.id,
          projectId: Number(payForm.projectId) || null,
          amount: Number(payForm.amount || 0),
          paymentFor: payForm.paymentFor,
          method: payForm.method,
          date: payForm.date,
          trxId: payForm.trxId || '',
        });
        if (gatewayResult?.checkoutUrl) {
          window.location.href = gatewayResult.checkoutUrl;
          return;
        }
      } catch {
        gatewayResult = null;
      }
    }
    saveData({
      ...data,
      payments: [
        {
          id: Date.now(),
          memberId: member.id,
          type: payForm.paymentFor || 'মাসিক চাঁদা',
          amount: Number(payForm.amount || 0),
          date: payForm.date,
          method: payForm.method,
          projectId: Number(payForm.projectId) || null,
          trxId: gatewayResult?.transactionId || payForm.trxId || `SGPAY-${Date.now()}`,
          status: gatewayResult?.status === 'manual_pending' ? 'pending' : 'approved',
          note: gatewayResult?.message || `${payForm.method} পেমেন্ট গেটওয়ে থেকে সদস্য পোর্টালে জমা`,
        },
        ...data.payments,
      ],
    });
    setPayForm({ amount: '', method: 'bKash', paymentFor: 'মাসিক চাঁদা', projectId: '', date: today() });
  };

  const submitAdvice = (event) => {
    event.preventDefault();
    saveData({
      ...data,
      advice: [
        {
          id: Date.now(),
          memberId: member.id,
          memberName: member.name,
          title: adviceForm.title,
          advice: adviceForm.advice,
          date: adviceForm.date,
        },
        ...(data.advice || []),
      ],
    });
    setAdviceForm({ title: '', advice: '', date: today() });
    setAdviceMessage('আপনার পরামর্শ সফলভাবে পাঠানো হয়েছে।');
  };

  const requestProfileUpdate = (event) => {
    event.preventDefault();
    saveData({
      ...data,
      memberRequests: [
        {
          id: Date.now(),
          type: 'update',
          memberId: member.id,
          status: 'pending',
          submittedAt: today(),
          payload: updateForm,
        },
        ...data.memberRequests,
      ],
    });
    setUpdateMessage('আপনার তথ্য আপডেট রিকোয়েস্ট অ্যাডমিন প্যানেলে পাঠানো হয়েছে। অনুমোদন হলে প্রোফাইল আপডেট হবে।');
  };

  return (
    <Shell title={`স্বাগতম, ${member.name}`} subtitle={`${member.userId} সদস্য পোর্টাল | ${syncStatusLabel(syncStatus)}`} navItems={nav} active={active} setActive={setActive} onLogout={onLogout}>
      {active === 'dashboard' && (
        <MemberHome
          member={member}
          payments={memberPayments}
          summary={summary}
          pendingRequests={pendingMemberRequests}
          data={data}
          form={payForm}
          setForm={setPayForm}
          onSubmit={requestPayment}
        />
      )}
      {active === 'organization' && <MemberOrganizationOverview data={data} />}
      {active === 'profile' && <Profile member={member} />}
      {active === 'update' && (
        <section className="panel">
          <div className="section-title">
            <h3>নিজের তথ্য আপডেট রিকোয়েস্ট</h3>
            <span>অ্যাডমিন অনুমোদন দিলে তথ্য বদলাবে</span>
          </div>
          <MemberRequestForm form={updateForm} setForm={setUpdateForm} onSubmit={requestProfileUpdate} submitLabel="আপডেট রিকোয়েস্ট পাঠান" />
          {updateMessage && <div className="success-box">{updateMessage}</div>}
        </section>
      )}
      {active === 'payments' && <MemberPayments payments={memberPayments} member={member} data={data} />}
      {active === 'statement' && <Statement payments={memberPayments} summary={summary} member={member} />}
      {active === 'projects' && <MemberProjects data={data} />}
      {active === 'advice' && (
        <MemberAdviceTab
          data={data}
          form={adviceForm}
          setForm={setAdviceForm}
          onSubmit={submitAdvice}
          message={adviceMessage}
          member={member}
        />
      )}
      {active === 'notice' && data.notices.map((notice) => <section className="panel notice" key={notice.id}><span>{notice.date}</span><h3>{notice.title}</h3><p>{notice.body}</p></section>)}
    </Shell>
  );
}

function MemberOrganizationOverview({ data }) {
  const totals = calculateTotals(data);
  const activeMembers = data.members.filter((member) => member.status === 'active').length;
  const pendingRequests = data.memberRequests.filter((request) => request.status === 'pending');
  const approvedPayments = data.payments.filter((payment) => payment.status === 'approved');
  const pendingPayments = data.payments.filter((payment) => payment.status === 'pending');
  const monthlyPremiumTotal = approvedPayments
    .filter((payment) => payment.type === 'মাসিক চাঁদা')
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const latestPayments = [...data.payments].slice(0, 6);
  const latestExpenses = [...data.expenses].slice(0, 6);
  const latestRequests = [...data.memberRequests].slice(0, 5);
  const latestNotices = [...data.notices].slice(0, 4);
  const committeeNames = Array.from(
    new Set([
      ...(data.committeeMembers || []).map((member) => member.committeeName || defaultCommitteeName),
      ...Object.keys(data.committeeDetails || {}),
    ])
  );
  const groupedCommittees = committeeNames
    .map((committeeName) => ({
      committeeName,
      details: (data.committeeDetails || {})[committeeName] || {},
      members: (data.committeeMembers || []).filter((member) => (member.committeeName || defaultCommitteeName) === committeeName),
    }))
    .filter((group) => group.members.length > 0 || Object.values(group.details).some(Boolean));
  const memberCapacity = `${data.members.length}/৪০ জন`;

  return (
    <div className="stack">
      <section className="admin-hero member-organization-hero">
        <div>
          <span>সবর গার্ডেন সদস্য তথ্য কেন্দ্র</span>
          <h2>সংগঠন, কমিটি, প্রকল্প ও আর্থিক সারাংশ</h2>
          <p>সদস্যরা এখানে এডমিন ড্যাশবোর্ডের গুরুত্বপূর্ণ তথ্য, কমিটির দায়িত্ব এবং প্রকল্পের অবস্থা দেখতে পারবেন।</p>
        </div>
        <div className="admin-hero-badge">
          <Users size={34} />
          <strong>{memberCapacity}</strong>
          <span>বর্তমান সদস্য অবস্থা</span>
        </div>
      </section>

      <div className="member-stat-grid">
        <ColorStatCard tone="green" icon={Banknote} label="মোট জমা" value={currency(totals.deposits)} />
        <ColorStatCard tone="blue" icon={Receipt} label="মোট ব্যয়" value={currency(totals.expenses)} />
        <ColorStatCard tone="purple" icon={Wallet} label="বর্তমান ব্যালেন্স" value={currency(totals.balance)} />
        <ColorStatCard tone="amber" icon={ShieldCheck} label="অপেক্ষমান রিকোয়েস্ট" value={`${pendingRequests.length}টি`} />
        <ColorStatCard tone="green" icon={CreditCard} label="মাসিক চাঁদা" value={currency(monthlyPremiumTotal)} />
        <ColorStatCard tone="blue" icon={Landmark} label="জমি প্রকল্প" value={`${data.landProjects.length}টি`} />
        <ColorStatCard tone="purple" icon={UserRoundCog} label="কমিটি" value={`${groupedCommittees.length}টি`} />
        <ColorStatCard tone="amber" icon={CalendarDays} label="নোটিশ" value={`${data.notices.length}টি`} />
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>এডমিন ড্যাশবোর্ড সারাংশ</h3>
            <span>সদস্যদের জন্য read-only তথ্য</span>
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="সদস্য ধারণক্ষমতা" value={memberCapacity} />
            <Info label="সক্রিয় সদস্য" value={`${activeMembers} জন`} />
            <Info label="অনুমোদিত পেমেন্ট" value={`${approvedPayments.length}টি`} />
            <Info label="অপেক্ষমান পেমেন্ট" value={`${pendingPayments.length}টি`} />
            <Info label="রিফান্ড" value={currency(totals.refunds)} />
            <Info label="ব্যাংক হিসাব" value="UCB - 7863241001126362" />
          </div>
        </section>

        <section className="panel">
          <div className="section-title">
            <h3>প্রকল্প ও সংগঠন অবস্থা</h3>
            <span>জমি, কমিটি ও নোটিশ</span>
          </div>
          <DataTable
            headers={['ধরন', 'নাম', 'স্ট্যাটাস/সংখ্যা']}
            rows={[
              ...data.landProjects.slice(0, 5).map((project) => ['জমি প্রকল্প', project.name, project.status || '-']),
              ...groupedCommittees.slice(0, 5).map((group) => ['কমিটি', group.committeeName, `${group.members.length} জন`]),
              ...latestNotices.slice(0, 3).map((notice) => ['নোটিশ', notice.title, notice.date]),
            ]}
          />
        </section>
      </div>

      <section className="panel">
        <div className="section-title">
          <h3>কমিটি তথ্য, কাজ ও দায়িত্ব</h3>
          <span>{groupedCommittees.length}টি কমিটি</span>
        </div>
        <div className="committee-overview-stack">
          {groupedCommittees.length === 0 && <p className="muted-text">এখনও কোনো কমিটি তথ্য যোগ করা হয়নি।</p>}
          {groupedCommittees.map((group) => (
            <article className="committee-readonly-block" key={group.committeeName}>
              <div className="section-title">
                <h3>{group.committeeName}</h3>
                <span>{group.members.length} জন সদস্য</span>
              </div>
              <div className="committee-detail-summary member-committee-summary">
                <Info label="কাজ" value={group.details.work || 'এখনও লেখা হয়নি'} />
                <Info label="পলিসি / নীতিমালা" value={group.details.policy || 'এখনও লেখা হয়নি'} />
                <Info label="দায়িত্ব" value={group.details.duties || 'এখনও লেখা হয়নি'} />
                <Info label="ক্ষমতা / বিশেষ শর্ত" value={group.details.authority || 'এখনও লেখা হয়নি'} />
              </div>
              <div className="committee-grid">
                {group.members.map((committeeMember) => (
                  <div className="committee-card" key={committeeMember.id}>
                    <div className="card-topline">
                      <MemberThumb member={committeeMember} />
                      <span className="status-pill">{statusLabel(committeeMember.status)}</span>
                    </div>
                    <strong>{committeeMember.position}</strong>
                    <span>{committeeMember.name}</span>
                    <small>{committeeMember.userId || 'সদস্য আইডি নেই'}</small>
                    <small>{committeeMember.mobile || 'মোবাইল নেই'}</small>
                    <small>{committeeMember.address || 'ঠিকানা নেই'}</small>
                    <small>{committeeMember.termStart || '-'} - {committeeMember.termEnd || '-'}</small>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>সাম্প্রতিক পেমেন্ট</h3>
            <span>{latestPayments.length}টি রেকর্ড</span>
          </div>
          <DataTable
            headers={['তারিখ', 'সদস্য', 'ধরন', 'টাকা', 'স্ট্যাটাস']}
            rows={latestPayments.map((item) => [
              item.date,
              memberName(data, item.memberId),
              item.type,
              currency(item.amount),
              statusLabel(item.status),
            ])}
          />
        </section>

        <section className="panel">
          <div className="section-title">
            <h3>সাম্প্রতিক ব্যয়</h3>
            <span>{latestExpenses.length}টি রেকর্ড</span>
          </div>
          <DataTable
            headers={['তারিখ', 'কাজ', 'ক্যাটাগরি', 'টাকা']}
            rows={latestExpenses.map((item) => [item.date, item.title, item.category, currency(item.amount)])}
          />
        </section>
      </div>

      <section className="panel">
        <div className="section-title">
          <h3>সাম্প্রতিক সদস্য রিকোয়েস্ট</h3>
          <span>নিবন্ধন/আপডেট অনুমোদন অবস্থা</span>
        </div>
        <DataTable
          headers={['তারিখ', 'ধরন', 'নাম', 'স্ট্যাটাস']}
          rows={latestRequests.map((request) => [
            request.submittedAt,
            request.type === 'registration' ? 'নতুন নিবন্ধন' : 'তথ্য আপডেট',
            request.payload?.name || memberName(data, request.memberId),
            statusLabel(request.status),
          ])}
        />
      </section>
    </div>
  );
}

function MemberProjects({ data }) {
  return (
    <div className="stack">
      <section className="admin-hero project-member-hero">
        <div>
          <span>সদস্য প্রকল্প তথ্য</span>
          <h2>সকল জমি প্রকল্পের তথ্য ও ডকুমেন্ট</h2>
          <p>লগইন করা সদস্যরা প্রকল্পের অবস্থান, বাজেট, যাচাই তথ্য, হিসাব ও সংরক্ষিত ডকুমেন্ট দেখতে পারবেন।</p>
        </div>
        <div className="admin-hero-badge">
          <span>মোট প্রকল্প</span>
          <strong>{data.landProjects.length}টি</strong>
        </div>
      </section>

      <div className="project-grid">
        {data.landProjects.map((project) => {
          const financial = projectFinancialSummary(data, project.id);
          return (
            <section className="panel project-card" key={project.id}>
              <div className="card-topline">
                <Landmark size={30} />
                <span className="status-pill">{project.status || '-'}</span>
              </div>
              <h3>{project.name}</h3>
              <p>
                <MapPin size={16} /> {project.location}
              </p>
              <div className="info-grid">
                <Info label="জমির আকার" value={project.size} />
                <Info label="প্রাক্কলিত বাজেট" value={currency(project.budget)} />
                <Info label="খতিয়ান" value={project.khatian || '-'} />
                <Info label="দলিল" value={project.deed || '-'} />
                <Info label="নামজারি" value={project.mutation || '-'} />
                <Info label="নিট হিসাব" value={currency(financial.balance)} />
              </div>
              {project.note && <p className="muted-text">{project.note}</p>}
              <div className="check-list">
                {(project.checks || []).map((item) => (
                  <span key={item}>
                    <BadgeCheck size={16} /> {item}
                  </span>
                ))}
              </div>
              <ProjectDocumentList documents={project.documents || []} />
            </section>
          );
        })}
      </div>
    </div>
  );
}

function MemberHome({ member, payments, summary, pendingRequests, data, form, setForm, onSubmit }) {
  const approvedPayments = payments.filter((payment) => payment.status === 'approved');
  const pendingPayments = payments.filter((payment) => payment.status === 'pending');
  const latestPayments = payments.slice(0, 5);
  const monthlyTotal = approvedPayments
    .filter((payment) => payment.type === 'মাসিক চাঁদা')
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const profileFields = [
    member.photo,
    member.name,
    member.mobile,
    member.nid,
    member.father,
    member.mother,
    member.permanentAddress,
    member.currentAddress,
    member.nominee?.name,
    member.nominee?.mobile,
    member.nominee?.nid,
    member.nominee?.photo,
  ];
  const profilePercent = Math.round((profileFields.filter(Boolean).length / profileFields.length) * 100);

  return (
    <div className="stack">
      <section className="member-hero">
        <div className="member-hero-profile">
          <div className="avatar large-avatar">
            {member.photo ? <img src={member.photo} alt={member.name} /> : <User size={42} />}
          </div>
          <div>
            <span>সদস্য পোর্টাল</span>
            <h2>{member.name}</h2>
            <p>{member.userId} | {statusLabel(member.status)} | {member.mobile}</p>
          </div>
        </div>
        <div className="member-hero-balance">
          <span>নিট ব্যালেন্স</span>
          <strong>{currency(summary.balance)}</strong>
        </div>
      </section>

      <div className="member-stat-grid">
        <ColorStatCard tone="green" icon={Wallet} label="মোট জমা" value={currency(summary.deposits)} />
        <ColorStatCard tone="blue" icon={CreditCard} label="মাসিক চাঁদা" value={currency(monthlyTotal)} />
        <ColorStatCard tone="amber" icon={FileText} label="অপেক্ষমান পেমেন্ট" value={`${pendingPayments.length}টি`} />
        <ColorStatCard tone="purple" icon={ShieldCheck} label="আপডেট রিকোয়েস্ট" value={`${pendingRequests.length}টি`} />
      </div>

      <div className="dashboard-grid">
        <section className="panel member-color-panel">
          <div className="section-title">
            <h3>প্রোফাইল সম্পূর্ণতা</h3>
            <span>{profilePercent}% সম্পন্ন</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${profilePercent}%` }} />
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="NID" value={member.nid} />
            <Info label="নমিনি" value={member.nominee?.name} />
            <Info label="বর্তমান ঠিকানা" value={member.currentAddress} />
            <Info label="সদস্য হওয়ার তারিখ" value={member.joinedAt} />
          </div>
        </section>

        <section className="panel member-color-panel warm">
          <div className="section-title">
            <h3>দ্রুত হিসাব</h3>
            <span>অনুমোদিত হিস্টরি</span>
          </div>
          <div className="info-grid dashboard-info-grid">
            <Info label="অনুমোদিত পেমেন্ট" value={`${approvedPayments.length}টি`} />
            <Info label="রিফান্ড" value={currency(summary.refunds)} />
            <Info label="প্রাথমিক বিনিয়োগ" value={currency(member.investment)} />
            <Info label="সর্বশেষ পেমেন্ট" value={latestPayments[0]?.date || '-'} />
          </div>
        </section>
      </div>

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-title">
            <h3>সাম্প্রতিক পেমেন্ট হিস্টরি</h3>
            <span>{latestPayments.length}টি রেকর্ড</span>
          </div>
          <DataTable
            headers={['তারিখ', 'ধরন', 'টাকা', 'মেথড', 'স্ট্যাটাস']}
            rows={latestPayments.map((payment) => [
              payment.date,
              payment.type,
              currency(payment.amount),
              payment.method,
              statusLabel(payment.status),
            ])}
          />
        </section>

        <PaymentRequest data={data} form={form} setForm={setForm} onSubmit={onSubmit} compact />
      </div>
    </div>
  );
}

function ColorStatCard({ tone, icon: Icon, label, value }) {
  return (
    <article className={`color-stat-card ${tone}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <Icon size={30} />
    </article>
  );
}

function PaymentRequest({ data, form, setForm, onSubmit }) {
  return (
    <section className="panel">
      <div className="section-title">
        <h3>পেমেন্ট গেটওয়ে দিয়ে জমা দিন</h3>
        <span>bKash, Nagad, Rocket, কার্ড বা ব্যাংক ট্রান্সফার</span>
      </div>
      <div className="gateway-grid">
        {['bKash', 'Nagad', 'Rocket', 'Card', 'Bank Transfer'].map((gateway) => (
          <button
            className={form.method === gateway ? 'gateway-card selected' : 'gateway-card'}
            key={gateway}
            type="button"
            onClick={() => setForm({ ...form, method: gateway })}
          >
            <CreditCard size={20} />
            <span>{gateway}</span>
          </button>
        ))}
      </div>
      <form className="form-grid" onSubmit={onSubmit}>
        <label>
          পেমেন্টের ধরন
          <select value={form.paymentFor || 'মাসিক চাঁদা'} onChange={(event) => setForm({ ...form, paymentFor: event.target.value })}>
            <option>মাসিক চাঁদা</option>
            <option>প্রাথমিক বিনিয়োগ</option>
            <option>অতিরিক্ত বিনিয়োগ</option>
            <option>উন্নয়ন ব্যয়</option>
          </select>
        </label>
        <Input label="টাকার পরিমাণ" name="amount" type="number" form={form} setForm={setForm} required />
        <Input label="তারিখ" name="date" type="date" form={form} setForm={setForm} required />
        <label>
          প্রকল্প
          <select value={form.projectId || ''} onChange={(event) => setForm({ ...form, projectId: event.target.value })}>
            <option value="">সাধারণ চাঁদা/ফান্ড</option>
            {data.landProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <Input label="ট্রানজেকশন আইডি" name="trxId" form={form} setForm={setForm} />
        <div className="gateway-note wide">
          নির্বাচিত গেটওয়ে: <strong>{form.method}</strong>. আসল মার্চেন্ট API যুক্ত হলে এখানে checkout/callback চলবে।
        </div>
        <button className="primary-button form-action" type="submit">
          <CreditCard size={18} /> পেমেন্ট সম্পন্ন করুন
        </button>
      </form>
    </section>
  );
}

function Profile({ member }) {
  return (
    <section className="panel">
      <div className="profile-header">
        <div className="avatar">
          {member.photo ? <img src={member.photo} alt={member.name} /> : <User size={36} />}
        </div>
        <div>
          <h3>{member.name}</h3>
          <span>{member.userId}</span>
        </div>
      </div>
      <div className="info-grid">
        <Info label="পিতার নাম" value={member.father} />
        <Info label="মাতার নাম" value={member.mother} />
        <Info label="মোবাইল" value={member.mobile} />
        <Info label="NID" value={member.nid} />
        <Info label="পেশা" value={member.profession} />
        <Info label="ইমেইল" value={member.email || '-'} />
        <Info label="স্থায়ী ঠিকানা" value={member.permanentAddress} />
        <Info label="বর্তমান ঠিকানা" value={member.currentAddress} />
        <Info label="জরুরি যোগাযোগ" value={member.emergency || '-'} />
      </div>
      <div className="divider" />
      <h3>নমিনি তথ্য</h3>
      <div className="info-grid">
        <Info label="নমিনির নাম" value={member.nominee.name || '-'} />
        <Info label="সম্পর্ক" value={member.nominee.relation || '-'} />
        <Info label="মোবাইল" value={member.nominee.mobile || '-'} />
        <Info label="NID" value={member.nominee.nid || '-'} />
        <Info label="ঠিকানা" value={member.nominee.address || '-'} />
      </div>
      {member.nominee.photo && (
        <div className="nominee-photo-block">
          <span>নমিনির ছবি</span>
          <img src={member.nominee.photo} alt={member.nominee.name || 'Nominee'} />
        </div>
      )}
    </section>
  );
}

function MemberPayments({ payments, member, data }) {
  const [receiptPayment, setReceiptPayment] = useState(null);
  const approvedPayments = payments.filter((payment) => payment.status === 'approved');

  return (
    <>
      <section className="panel statement-head">
        <div>
          <h3>পেমেন্ট হিস্টরি ও রিসিট</h3>
          <p>এডমিন অনুমোদন করলে এখানে রিসিট দেখা, প্রিন্ট বা PDF ডাউনলোড করা যাবে।</p>
        </div>
        <span className="status-pill">{approvedPayments.length}টি রিসিট প্রস্তুত</span>
      </section>
      <section className="panel">
        <DataTable
          headers={['তারিখ', 'ধরন', 'টাকা', 'মেথড', 'ট্রানজেকশন', 'স্ট্যাটাস', 'রিসিট']}
          rows={payments.map((item) => [
            item.date,
            item.type,
            currency(item.amount),
            item.method,
            item.trxId || '-',
            statusLabel(item.status),
            item.status === 'approved' ? (
              <button className="secondary-button small-button" type="button" onClick={() => setReceiptPayment(item)}>
                <Receipt size={14} /> রিসিট
              </button>
            ) : (
              <span className="muted-text">এপ্রুভ হলে পাওয়া যাবে</span>
            ),
          ])}
        />
      </section>
      {receiptPayment && (
        <PaymentReceiptModal
          payment={receiptPayment}
          member={member}
          data={data}
          onClose={() => setReceiptPayment(null)}
        />
      )}
    </>
  );
}

function PaymentReceiptModal({ payment, member, data, onClose }) {
  const receiptNumber = payment.receiptNo || receiptNo(payment);
  const project = projectName(data, payment.projectId);
  const printReceipt = () => window.print();

  return (
    <div className="modal-backdrop receipt-backdrop">
      <section className="document-modal receipt-modal">
        <div className="receipt-actions">
          <button className="secondary-button small-button" type="button" onClick={onClose}>
            <X size={15} /> বন্ধ
          </button>
          <button className="primary-button small-button" type="button" onClick={printReceipt}>
            <Download size={15} /> প্রিন্ট / PDF
          </button>
        </div>
        <div className="receipt-paper printable-receipt">
          <div className="receipt-header">
            <div>
              <span>সবর গার্ডেন</span>
              <h2>পেমেন্ট রিসিট</h2>
              <p>সম্মিলিত শক্তি, নিরাপদ বিনিয়োগ</p>
            </div>
            <div className="receipt-stamp">
              <BadgeCheck size={30} />
              <strong>অনুমোদিত</strong>
            </div>
          </div>
          <div className="receipt-meta">
            <Info label="রিসিট নম্বর" value={receiptNumber} />
            <Info label="তারিখ" value={payment.date} />
            <Info label="সদস্য আইডি" value={member.userId} />
            <Info label="সদস্য নাম" value={member.name} />
            <Info label="মোবাইল" value={member.mobile} />
            <Info label="পেমেন্ট ধরন" value={payment.type} />
            <Info label="মেথড" value={payment.method || '-'} />
            <Info label="ট্রানজেকশন আইডি" value={payment.trxId || '-'} />
            <Info label="প্রকল্প" value={project} />
            <Info label="অনুমোদনের তারিখ" value={payment.approvedAt || payment.date || '-'} />
          </div>
          <div className="receipt-amount">
            <span>প্রাপ্ত টাকা</span>
            <strong>{currency(payment.amount)}</strong>
          </div>
          {payment.note && <p className="receipt-note">{payment.note}</p>}
          <div className="digital-receipt-notice">
            <strong>ডিজিটাল রিসিট নোটিশ:</strong> এই রিসিটটি সবর গার্ডেন অনলাইন পোর্টাল থেকে স্বয়ংক্রিয়ভাবে তৈরি ও সংরক্ষিত হয়েছে। অনুমোদিত পেমেন্ট রেকর্ডের ভিত্তিতে ইস্যুকৃত হওয়ায় এতে কোনো হাতে লেখা স্বাক্ষর বা সিলের প্রয়োজন নেই।
          </div>
          <div className="receipt-footer">
            <div>
              <span>সদস্য কপি</span>
            </div>
            <div>
              <span>সিস্টেম অনুমোদিত রিসিট</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Statement({ payments, summary, member }) {
  let running = 0;
  const rows = [...payments]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((item) => {
      const debit = item.type === 'রিফান্ড' ? item.amount : 0;
      const credit = debit ? 0 : item.amount;
      running += credit - debit;
      return [item.date, item.type, debit ? currency(debit) : '-', credit ? currency(credit) : '-', currency(running)];
    });

  return (
    <div className="stack">
      <section className="panel statement-head">
        <div>
          <h3>{member.name} এর আর্থিক স্টেটমেন্ট</h3>
          <p>মোট জমা {currency(summary.deposits)} | নিট ব্যালেন্স {currency(summary.balance)}</p>
        </div>
        <button className="secondary-button" onClick={() => window.print()}>
          <Download size={18} /> প্রিন্ট / PDF
        </button>
      </section>
      <section className="panel">
        <DataTable headers={['তারিখ', 'বিবরণ', 'ডেবিট', 'ক্রেডিট', 'ব্যালেন্স']} rows={rows} />
      </section>
    </div>
  );
}

function MemberRequestForm({ form, setForm, onSubmit, submitLabel }) {
  const isRegistration = submitLabel === 'আবেদন পাঠান';

  return (
    <form className="form-grid" onSubmit={onSubmit}>
      <FileInput label="সদস্যের ছবি" name="photo" form={form} setForm={setForm} required={isRegistration} />
      <Input label="পূর্ণ নাম" name="name" form={form} setForm={setForm} required />
      <Input label="মোবাইল নম্বর" name="mobile" form={form} setForm={setForm} required />
      <Input label="জাতীয় পরিচয়পত্র নম্বর" name="nid" form={form} setForm={setForm} required />
      <AttachmentInput label="NID / ডকুমেন্ট এটাচমেন্ট" name="attachment" form={form} setForm={setForm} required={isRegistration} wide={false} />
      <Input label="প্রাথমিক বিনিয়োগ" name="investment" type="number" form={form} setForm={setForm} span="third" />
      <Input label="পেশা" name="profession" form={form} setForm={setForm} span="third" />
      <Input label="ইমেইল" name="email" type="email" form={form} setForm={setForm} span="third" />
      <Input label="পিতার নাম" name="father" form={form} setForm={setForm} required />
      <Input label="মাতার নাম" name="mother" form={form} setForm={setForm} required />
      <Input label="ধর্ম" name="religion" form={form} setForm={setForm} required />
      <Input label="স্থায়ী ঠিকানা" name="permanentAddress" form={form} setForm={setForm} wide required />
      <Input label="বর্তমান ঠিকানা" name="currentAddress" form={form} setForm={setForm} wide required />
      <Input label="জরুরি যোগাযোগ" name="emergency" form={form} setForm={setForm} wide />
      <Input label="নমিনির নাম" name="nomineeName" form={form} setForm={setForm} required />
      <Input label="সম্পর্ক" name="nomineeRelation" form={form} setForm={setForm} required />
      <Input label="নমিনির পিতা/স্বামীর নাম" name="nomineeFather" form={form} setForm={setForm} required />
      <Input label="নমিনির মোবাইল" name="nomineeMobile" form={form} setForm={setForm} required />
      <Input label="নমিনির NID" name="nomineeNid" form={form} setForm={setForm} required />
      <FileInput label="নমিনির ছবি" name="nomineePhoto" form={form} setForm={setForm} required={isRegistration} />
      <Input label="নমিনির ঠিকানা" name="nomineeAddress" form={form} setForm={setForm} wide />
      <button className="primary-button form-action" type="submit">
        <Save size={18} /> {submitLabel}
      </button>
    </form>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <article className="stat-card">
      <Icon size={28} />
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function fieldClassName({ wide, span }) {
  return [wide ? 'wide' : '', span === 'third' ? 'third' : ''].filter(Boolean).join(' ');
}

function Input({ label, name, form, setForm, type = 'text', required = false, wide = false, span }) {
  return (
    <label className={fieldClassName({ wide, span })}>
      <RequiredLabel label={label} required={required} />
      <input
        type={type}
        value={form[name] || ''}
        onChange={(event) => setForm({ ...form, [name]: event.target.value })}
        required={required}
      />
    </label>
  );
}

function TextArea({ label, name, value, onChange }) {
  return (
    <label className="wide">
      {label}
      <textarea value={value || ''} onChange={(event) => onChange(name, event.target.value)} rows={4} />
    </label>
  );
}

function RequiredLabel({ label, required }) {
  return (
    <span>
      {label}
      {required && <span className="required-mark"> *</span>}
    </span>
  );
}

function FileInput({ label, name, form, setForm, required = false }) {
  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const uploaded = await uploadFileToServer(file);
      if (uploaded?.url) {
        setForm({ ...form, [name]: uploaded.url, [`${name}File`]: uploaded });
        return;
      }
    } catch {
      // Local fallback keeps the demo usable when backend upload is unavailable.
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm({ ...form, [name]: reader.result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <label className="wide">
      <RequiredLabel label={label} required={required} />
      <input type="file" accept="image/*" onChange={handleFile} required={required && !form[name]} />
      {form[name] && (
        <div className="photo-preview">
          <img src={form[name]} alt="Preview" />
          <button className="secondary-button small-button" type="button" onClick={() => setForm({ ...form, [name]: '' })}>
            ছবি সরান
          </button>
        </div>
      )}
    </label>
  );
}

function AttachmentInput({ label, name, form, setForm, required = false, wide = true, span }) {
  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const uploaded = await uploadFileToServer(file);
      if (uploaded) {
        setForm({ ...form, [name]: uploaded });
        return;
      }
    } catch {
      // Local fallback keeps the demo usable when backend upload is unavailable.
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm({
        ...form,
        [name]: {
          name: file.name,
          type: file.type,
          size: file.size,
          dataUrl: reader.result,
        },
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <label className={fieldClassName({ wide, span })}>
      <RequiredLabel label={label} required={required} />
      <input type="file" accept="image/*,.pdf" onChange={handleFile} required={required && !form[name]} />
      {form[name]?.name && (
        <div className="attachment-preview">
          <span>{form[name].name}</span>
          <button className="secondary-button small-button" type="button" onClick={() => setForm({ ...form, [name]: null })}>
            ফাইল সরান
          </button>
        </div>
      )}
    </label>
  );
}

function ProjectDocumentsInput({ label, form, setForm }) {
  const documents = form.documents || [];

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const uploadedDocuments = [];
    if (isApiEnabled) {
      for (const file of files) {
        try {
          const uploaded = await uploadFileToServer(file);
          if (uploaded) uploadedDocuments.push(uploaded);
        } catch {
          // Failed uploads will fall back to local preview below.
        }
      }
    }

    const remainingFiles = uploadedDocuments.length === files.length ? [] : files.slice(uploadedDocuments.length);
    Promise.all(
      remainingFiles.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                id: `${Date.now()}-${file.name}`,
                name: file.name,
                type: file.type,
                size: file.size,
                dataUrl: reader.result,
                uploadedAt: today(),
              });
            reader.readAsDataURL(file);
          })
      )
    ).then((newDocuments) => {
      setForm({ ...form, documents: [...documents, ...uploadedDocuments, ...newDocuments] });
      event.target.value = '';
    });
  };

  const removeDocument = (docId) => {
    setForm({ ...form, documents: documents.filter((doc) => (doc.id || doc.name) !== docId) });
  };

  return (
    <label className="wide">
      {label}
      <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" multiple onChange={handleFiles} />
      <ProjectDocumentList documents={documents} onRemove={removeDocument} editable />
    </label>
  );
}

function ProjectDocumentList({ documents = [], onRemove, editable = false }) {
  const [viewingDoc, setViewingDoc] = useState(null);

  if (!documents.length) {
    return <div className="empty-documents">কোনো ডকুমেন্ট যুক্ত করা হয়নি</div>;
  }

  return (
    <>
      <div className="project-documents">
        {documents.map((doc, index) => {
          const docKey = doc.id || doc.name || index;
          return (
            <div className="project-document" key={docKey}>
              <FileText size={18} />
              <div>
                <strong>{doc.name || 'ডকুমেন্ট'}</strong>
                <span>{doc.uploadedAt ? `আপলোড: ${formatBanglaDate(doc.uploadedAt)}` : 'সংরক্ষিত ডকুমেন্ট'}</span>
              </div>
              {documentUrl(doc) && (
                <button className="secondary-button small-button" type="button" onClick={() => setViewingDoc(doc)}>
                  <Eye size={14} /> ভিউ
                </button>
              )}
              {documentUrl(doc) && (
                <a className="secondary-button small-button" href={documentUrl(doc)} download={doc.name || 'project-document'}>
                  <Download size={14} /> ডাউনলোড
                </a>
              )}
              {editable && (
                <button className="secondary-button small-button" type="button" onClick={() => onRemove(docKey)}>
                  <Trash2 size={14} /> সরান
                </button>
              )}
            </div>
          );
        })}
      </div>
      {viewingDoc && <DocumentPreviewModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />}
    </>
  );
}

function DocumentPreviewModal({ doc, onClose }) {
  const url = documentUrl(doc);
  const isImage = doc.type?.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(doc.name || '');
  const isPdf = doc.type === 'application/pdf' || /\.pdf$/i.test(doc.name || '');

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="document-modal">
        <div className="section-title">
          <div>
            <h3>{doc.name || 'ডকুমেন্ট ভিউ'}</h3>
            <span>ডকুমেন্ট দেখা ও ডাউনলোড</span>
          </div>
          <button className="secondary-button" type="button" onClick={onClose}>
            <X size={16} /> বন্ধ
          </button>
        </div>
        <div className="document-preview-frame">
          {isImage && <img src={url} alt={doc.name || 'Project document'} />}
          {isPdf && !isImage && <iframe title={doc.name || 'Project document'} src={url} />}
          {!isImage && !isPdf && (
            <div className="document-preview-empty">
              <FileText size={42} />
              <strong>এই ফাইলটি ব্রাউজারে সরাসরি দেখা নাও যেতে পারে</strong>
              <span>ডাউনলোড করে খুললে সম্পূর্ণ ডকুমেন্ট দেখা যাবে।</span>
            </div>
          )}
        </div>
        <a className="primary-button full-button" href={url} download={doc.name || 'project-document'}>
          <Download size={18} /> ডাউনলোড করুন
        </a>
      </div>
    </div>
  );
}

function MemberThumb({ member }) {
  return (
    <div className="member-thumb">
      {member?.photo ? <img src={member.photo} alt={member.name || 'Member'} /> : <User size={18} />}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="info-item">
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </div>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <tr key={`${row[0]}-${index}`}>{row.map((cell, cellIndex) => <td key={`${cell}-${cellIndex}`}>{cell}</td>)}</tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length}>কোন তথ্য পাওয়া যায়নি</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function calculateTotals(data) {
  const deposits = data.payments
    .filter((item) => item.status !== 'rejected' && item.type !== 'রিফান্ড')
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const refunds = data.payments.filter((item) => item.type === 'রিফান্ড').reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const expenses = data.expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  return { deposits, refunds, expenses, balance: deposits - refunds - expenses };
}

function periodLabel(period) {
  const labels = {
    daily: 'আজকের রিপোর্ট',
    weekly: 'এই সপ্তাহের রিপোর্ট',
    monthly: 'এই মাসের রিপোর্ট',
    yearly: 'এই বছরের রিপোর্ট',
    all: 'সর্বমোট রিপোর্ট',
  };
  return labels[period] || labels.monthly;
}

function isInPeriod(dateString, period) {
  if (period === 'all') return true;
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  if (period === 'daily') {
    return date.toDateString() === start.toDateString();
  }

  if (period === 'weekly') {
    const day = start.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() - diff);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);
    return date >= weekStart && date < weekEnd;
  }

  if (period === 'monthly') {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }

  if (period === 'yearly') {
    return date.getFullYear() === now.getFullYear();
  }

  return true;
}

function addGroupedAmount(group, key, amount) {
  const safeKey = key || 'অন্যান্য';
  group[safeKey] = (group[safeKey] || 0) + Number(amount || 0);
}

function buildReport(data, period) {
  const payments = data.payments.filter((payment) => payment.status !== 'rejected' && isInPeriod(payment.date, period));
  const expenses = data.expenses.filter((expense) => isInPeriod(expense.date, period));
  const byPaymentType = {};
  const byExpenseCategory = {};

  payments.forEach((payment) => addGroupedAmount(byPaymentType, payment.type, payment.amount));
  expenses.forEach((expense) => addGroupedAmount(byExpenseCategory, expense.category, expense.amount));

  const income = payments
    .filter((payment) => payment.type !== 'রিফান্ড')
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const refunds = payments.filter((payment) => payment.type === 'রিফান্ড').reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const expense = expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const paymentRows = payments.map((payment) => ({
    date: payment.date,
    kind: 'আয়',
    description: `${memberName(data, payment.memberId)} - ${payment.type}`,
    income: payment.type === 'রিফান্ড' ? 0 : payment.amount,
    expense: payment.type === 'রিফান্ড' ? payment.amount : 0,
  }));
  const expenseRows = expenses.map((expenseItem) => ({
    date: expenseItem.date,
    kind: 'ব্যয়',
    description: `${expenseItem.title} - ${expenseItem.category || 'সাধারণ'}`,
    income: 0,
    expense: expenseItem.amount,
  }));

  return {
    income,
    refunds,
    expense,
    balance: income - refunds - expense,
    paymentCount: payments.length,
    expenseCount: expenses.length,
    byPaymentType,
    byExpenseCategory,
    rows: [...paymentRows, ...expenseRows].sort((a, b) => b.date.localeCompare(a.date)),
  };
}

function memberSummary(payments) {
  const deposits = payments.filter((item) => item.type !== 'রিফান্ড').reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const refunds = payments.filter((item) => item.type === 'রিফান্ড').reduce((sum, item) => sum + Number(item.amount || 0), 0);
  return { deposits, refunds, balance: deposits - refunds };
}

function memberName(data, id) {
  const member = data.members.find((item) => item.id === Number(id));
  return member ? `${member.name} (${member.userId})` : '-';
}

function projectName(data, id) {
  const project = data.landProjects.find((item) => item.id === Number(id));
  return project ? project.name : 'সাধারণ';
}

function receiptNo(payment) {
  return `SGR-${String(payment?.id || Date.now()).slice(-8)}`;
}

function documentUrl(doc) {
  if (!doc) return '';
  const rawUrl = doc.url || doc.dataUrl || '';
  if (!rawUrl) return '';
  if (/^(https?:|data:|blob:)/i.test(rawUrl)) return rawUrl;
  if (rawUrl.startsWith('/')) return rawUrl;
  return `${API_BASE_URL.replace(/\/api$/, '')}/${rawUrl.replace(/^\//, '')}`;
}

function syncStatusLabel(status) {
  const labels = {
    connecting: 'লোকাল + সার্ভার সংযোগ হচ্ছে',
    saving: 'লোকালে সেভ হয়েছে, সার্ভারে sync হচ্ছে',
    online: 'লোকাল + লাইভ ডাটাবেস সংযুক্ত',
    synced: 'লোকাল + সার্ভার দুটিই আপডেটেড',
    offline: 'সার্ভার বন্ধ, লোকাল মোড সক্রিয়; পরে auto sync হবে',
    local: 'লোকাল মোড সক্রিয়',
  };
  return labels[status] || labels.local;
}

function defaultAdminPermissions() {
  return adminModules.reduce((result, module) => {
    result[module.id] = module.actions.reduce((actions, action) => {
      actions[action] = module.id === 'dashboard' && action === 'view';
      return actions;
    }, {});
    return result;
  }, {});
}

function normalizeAdminPermissions(permissions) {
  const defaults = defaultAdminPermissions();
  Object.entries(permissions || {}).forEach(([moduleId, actions]) => {
    defaults[moduleId] = { ...(defaults[moduleId] || {}), ...(actions || {}) };
  });
  return defaults;
}

function allAdminPermissions() {
  return adminModules.reduce((result, module) => {
    result[module.id] = module.actions.reduce((actions, action) => {
      actions[action] = true;
      return actions;
    }, {});
    return result;
  }, {});
}

function getAdminPermissions(data, session) {
  if (session?.isSuperAdmin || !session?.adminUserId) return allAdminPermissions();
  const adminUser = (data.adminUsers || []).find((item) => item.id === session.adminUserId);
  if (!adminUser || adminUser.status === 'inactive') return { dashboard: { view: true } };
  return normalizeAdminPermissions(adminUser.permissions);
}

function hasAdminPermission(permissions, moduleId, action = 'view') {
  if (moduleId === 'dashboard' && action === 'view') return true;
  return Boolean(permissions?.[moduleId]?.[action]);
}

function defaultAdminUserForm() {
  return {
    status: 'active',
    permissions: defaultAdminPermissions(),
  };
}

function permissionSummary(permissions) {
  const normalized = normalizeAdminPermissions(permissions);
  return adminModules
    .filter((module) => module.actions.some((action) => normalized[module.id]?.[action]))
    .map((module) => module.label)
    .join(', ') || '-';
}

function projectFinancialSummary(data, projectId, period = 'all') {
  const payments = data.payments.filter(
    (payment) => payment.status !== 'rejected' && Number(payment.projectId) === Number(projectId) && isInPeriod(payment.date, period)
  );
  const expenses = data.expenses.filter(
    (expense) => Number(expense.projectId) === Number(projectId) && isInPeriod(expense.date, period)
  );
  const income = payments
    .filter((payment) => payment.type !== 'রিফান্ড')
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const refunds = payments
    .filter((payment) => payment.type === 'রিফান্ড')
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const expense = expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  return { income, refunds, expense, balance: income - refunds - expense };
}

function buildProjectReport(data, period) {
  return data.landProjects.map((project) => {
    const summary = projectFinancialSummary(data, project.id, period);
    return {
      id: project.id,
      name: project.name,
      status: project.status,
      budget: project.budget,
      documentCount: (project.documents || []).length,
      ...summary,
    };
  });
}

function buildMemberFromPayload(payload, base) {
  return {
    id: base.id,
    userId: base.userId,
    name: payload.name || '',
    father: payload.father || '',
    mother: payload.mother || '',
    mobile: payload.mobile || '',
    photo: payload.photo || '',
    attachment: payload.attachment || null,
    nid: payload.nid || '',
    email: payload.email || '',
    profession: payload.profession || '',
    religion: payload.religion || '',
    permanentAddress: payload.permanentAddress || '',
    currentAddress: payload.currentAddress || '',
    emergency: payload.emergency || '',
    investment: Number(payload.investment || 0),
    status: base.status || 'active',
    joinedAt: base.joinedAt || today(),
    nominee: {
      name: payload.nomineeName || '',
      relation: payload.nomineeRelation || '',
      father: payload.nomineeFather || '',
      mobile: payload.nomineeMobile || '',
      nid: payload.nomineeNid || '',
      photo: payload.nomineePhoto || '',
      address: payload.nomineeAddress || '',
    },
  };
}

function memberToForm(member) {
  return {
    name: member.name || '',
    father: member.father || '',
    mother: member.mother || '',
    mobile: member.mobile || '',
    photo: member.photo || '',
    attachment: member.attachment || null,
    nid: member.nid || '',
    email: member.email || '',
    profession: member.profession || '',
    religion: member.religion || '',
    permanentAddress: member.permanentAddress || '',
    currentAddress: member.currentAddress || '',
    emergency: member.emergency || '',
    investment: member.investment || 0,
    status: member.status || 'active',
    nomineeName: member.nominee?.name || '',
    nomineeRelation: member.nominee?.relation || '',
    nomineeFather: member.nominee?.father || '',
    nomineeMobile: member.nominee?.mobile || '',
    nomineeNid: member.nominee?.nid || '',
    nomineePhoto: member.nominee?.photo || '',
    nomineeAddress: member.nominee?.address || '',
  };
}

function statusLabel(status) {
  const labels = {
    active: 'সক্রিয়',
    suspended: 'স্থগিত',
    cancelled: 'বাতিল',
    approved: 'অনুমোদিত',
    pending: 'অপেক্ষমান',
    rejected: 'বাতিল',
    inactive: 'নিষ্ক্রিয়',
  };
  return labels[status] || status || '-';
}

export default App;
